import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockLoans, mockDocuments, mockConditions, mockActivities, documentTypes, loanPrograms, mockPricingScenarios, mockRateSheet, mockLockHistory, mockChatMessages, mockChatThreads, mockAIAnalysis, mockCOCRequests } from '../../services/mockData';
import featureFlagService from '../../services/featureFlagService';
import EditableField from '../../components/EditableField';
import DualAUSComponent from '../../components/broker/DualAUSComponent';
import FeesWorksheetComponent from '../../components/broker/FeesWorksheetComponent';
import FloatingAIAssistant from '../../components/broker/FloatingAIAssistant';
import COCRequestForm from '../../components/broker/COCRequestForm';
import COCRequestList from '../../components/broker/COCRequestList';
import COCRequestDetail from '../../components/broker/COCRequestDetail';
import { 
  PullCreditModal, 
  OrderAppraisalModal, 
  OpenTitleModal, 
  OrderVOAModal, 
  OrderVOIEModal 
} from '../../components/broker/SelfServiceModals';

const LoanDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [featureFlags, setFeatureFlags] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedLoan, setEditedLoan] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Tranche 5: Documents, Conditions, Activity
  const [documents, setDocuments] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [showBorrowerLinkModal, setShowBorrowerLinkModal] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);
  
  // Tranche 6: Pricing, Scenarios & Locking
  const [pricingScenarios, setPricingScenarios] = useState([]);
  const [lockHistory, setLockHistory] = useState([]);
  const [showRunPricingModal, setShowRunPricingModal] = useState(false);
  const [showLockRateModal, setShowLockRateModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showRateStackModal, setShowRateStackModal] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  
  // Tranche 14: AI Analysis & Self-Service Actions
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAIPanelLocked, setIsAIPanelLocked] = useState(false);
  const [showPullCreditModal, setShowPullCreditModal] = useState(false);
  const [showOrderAppraisalModal, setShowOrderAppraisalModal] = useState(false);
  const [showOpenTitleModal, setShowOpenTitleModal] = useState(false);
  const [showOrderVOAModal, setShowOrderVOAModal] = useState(false);
  const [showOrderVOIEModal, setShowOrderVOIEModal] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  
  // Tranche 7: Broker ↔ Lender Communication
  const [chatMessages, setChatMessages] = useState([]);
  const [chatThreads, setChatThreads] = useState([]);
  const [activeThread, setActiveThread] = useState('general');
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  
  // Tranche 9: AI Virtual Underwriter
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // COC (Change of Circumstance) State
  const [cocRequests, setCocRequests] = useState([]);
  const [showCOCForm, setShowCOCForm] = useState(false);
  const [selectedCOCRequest, setSelectedCOCRequest] = useState(null);

  // Load feature flags
  useEffect(() => {
    const loadFlags = () => {
      setFeatureFlags(featureFlagService.getFeatureFlags());
    };
    
    loadFlags();
    
    const handleStorageChange = (e) => {
      if (e.key === 'uff_broker_feature_flags') {
        loadFlags();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(loadFlags, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const foundLoan = mockLoans.find(l => l.id === parseInt(id));
      setLoan(foundLoan);
      setEditedLoan(foundLoan ? JSON.parse(JSON.stringify(foundLoan)) : null);
      
      // Load documents, conditions, and activities for this loan
      if (foundLoan) {
        setDocuments(mockDocuments.filter(d => d.loanId === foundLoan.id));
        setConditions(mockConditions.filter(c => c.loanId === foundLoan.id));
        setActivities(mockActivities.filter(a => a.loanId === foundLoan.id).sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        ));
        setPricingScenarios(mockPricingScenarios.filter(s => s.loanId === foundLoan.id));
        setLockHistory(mockLockHistory.filter(l => l.loanId === foundLoan.id));
        setChatMessages(mockChatMessages.filter(m => m.loanId === foundLoan.id));
        setChatThreads(mockChatThreads.filter(t => t.loanId === foundLoan.id));
        setCocRequests(mockCOCRequests.filter(c => c.loanId === foundLoan.id));
      }
      
      setLoading(false);
    }, 300);
  }, [id]);

  const isLoanLocked = loan?.loanDetails?.lockDate ? true : false;

  const isFeatureEnabled = (featureKey) => {
    return featureFlags[featureKey] === true;
  };

  const handleEditToggle = () => {
    if (isLoanLocked) {
      return; // Don't allow edit mode if loan is locked
    }
    if (isEditMode) {
      // Cancel edit - reset to original
      setEditedLoan(JSON.parse(JSON.stringify(loan)));
      setValidationErrors({});
    }
    setIsEditMode(!isEditMode);
    setSaveSuccess(false);
  };

  const handleFieldChange = (section, field, value, subField = null) => {
    setEditedLoan(prev => {
      const updated = { ...prev };
      if (subField) {
        updated[section] = { ...updated[section], [field]: { ...updated[section][field], [subField]: value } };
      } else if (section) {
        updated[section] = { ...updated[section], [field]: value };
      } else {
        updated[field] = value;
      }
      return updated;
    });
    // Clear validation error for this field
    if (validationErrors[`${section}.${field}${subField ? `.${subField}` : ''}`]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[`${section}.${field}${subField ? `.${subField}` : ''}`];
        return updated;
      });
    }
  };

  const validateLoan = () => {
    const errors = {};
    
    // Required borrower fields
    if (!editedLoan.borrower.firstName?.trim()) errors['borrower.firstName'] = 'First name is required';
    if (!editedLoan.borrower.lastName?.trim()) errors['borrower.lastName'] = 'Last name is required';
    if (!editedLoan.borrower.email?.trim()) errors['borrower.email'] = 'Email is required';
    if (!editedLoan.borrower.phone?.trim()) errors['borrower.phone'] = 'Phone is required';
    if (!editedLoan.borrower.ssn?.trim()) errors['borrower.ssn'] = 'SSN is required';
    if (!editedLoan.borrower.dateOfBirth) errors['borrower.dateOfBirth'] = 'Date of birth is required';
    if (!editedLoan.borrower.creditScore || editedLoan.borrower.creditScore < 300 || editedLoan.borrower.creditScore > 850) {
      errors['borrower.creditScore'] = 'Valid credit score (300-850) is required';
    }
    
    // Required employment fields
    if (!editedLoan.borrower.employment.employer?.trim()) errors['borrower.employment.employer'] = 'Employer is required';
    if (!editedLoan.borrower.employment.monthlyIncome || editedLoan.borrower.employment.monthlyIncome <= 0) {
      errors['borrower.employment.monthlyIncome'] = 'Monthly income is required';
    }
    
    // Required property fields
    if (!editedLoan.property.address?.trim()) errors['property.address'] = 'Address is required';
    if (!editedLoan.property.city?.trim()) errors['property.city'] = 'City is required';
    if (!editedLoan.property.state?.trim()) errors['property.state'] = 'State is required';
    if (!editedLoan.property.zip?.trim()) errors['property.zip'] = 'ZIP code is required';
    if (!editedLoan.property.propertyType?.trim()) errors['property.propertyType'] = 'Property type is required';
    if (!editedLoan.property.occupancy?.trim()) errors['property.occupancy'] = 'Occupancy is required';
    if (!editedLoan.property.purchasePrice || editedLoan.property.purchasePrice <= 0) {
      errors['property.purchasePrice'] = 'Purchase price is required';
    }
    
    // Required loan fields
    if (!editedLoan.loanDetails.loanAmount || editedLoan.loanDetails.loanAmount <= 0) {
      errors['loanDetails.loanAmount'] = 'Loan amount is required';
    }
    if (!editedLoan.loanDetails.interestRate || editedLoan.loanDetails.interestRate <= 0) {
      errors['loanDetails.interestRate'] = 'Interest rate is required';
    }
    if (!editedLoan.loanDetails.loanProgram?.trim()) errors['loanDetails.loanProgram'] = 'Loan program is required';
    if (!editedLoan.loanDetails.loanType?.trim()) errors['loanDetails.loanType'] = 'Loan type is required';
    
    return errors;
  };

  const handleSave = () => {
    const errors = validateLoan();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Save successful
    setLoan(JSON.parse(JSON.stringify(editedLoan)));
    setIsEditMode(false);
    setValidationErrors({});
    setSaveSuccess(true);
    
    // Hide success message after 3 seconds
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const displayLoan = isEditMode ? editedLoan : loan;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-success text-white';
      case 'Clear to Close':
        return 'bg-teal text-white';
      case 'In Underwriting':
        return 'bg-info text-white';
      case 'Conditions':
        return 'bg-warning text-white';
      case 'Processing':
        return 'bg-primary text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  const isLockExpiringSoon = (lockExpiration) => {
    const expirationDate = new Date(lockExpiration);
    const today = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 7;
  };

  // Tranche 14: Self-Service Action Handlers
  const handlePullCredit = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'credit_pull',
      description: `Credit report pulled (${data.creditType} pull, ${data.borrowerScope})`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowPullCreditModal(false);
    alert('Credit report ordered successfully! Results will be available within 2-3 minutes.');
  };

  const handleOrderAppraisal = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'appraisal_order',
      description: `Appraisal ordered (${data.appraisalType})`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOrderAppraisalModal(false);
    alert('Appraisal ordered successfully! Appraiser will be assigned within 24 hours.');
  };

  const handleOpenTitle = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'title_order',
      description: `Title order opened (${data.transactionType})`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOpenTitleModal(false);
    alert('Title order opened successfully! Title company will contact you within 1 business day.');
  };

  const handleOrderVOA = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'voa_order',
      description: `Digital asset verification (VOA) ordered`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOrderVOAModal(false);
    alert('VOA ordered successfully! Borrower will receive secure link to connect accounts.');
  };

  const handleOrderVOIE = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'voie_order',
      description: `Digital income/employment verification (VOIE) ordered`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOrderVOIEModal(false);
    alert('VOIE ordered successfully! Borrower will receive secure link to connect payroll.');
  };

  const handleCOCSubmit = (cocRequest) => {
    setCocRequests([cocRequest, ...cocRequests]);
    setShowCOCForm(false);
    
    const newActivity = {
      id: Date.now(),
      type: 'coc_submitted',
      description: `Change of Circumstance request submitted: ${cocRequest.categoryName}`,
      user: cocRequest.submittedBy,
      timestamp: cocRequest.submittedDate,
      details: cocRequest
    };
    setActivities([newActivity, ...activities]);
    
    alert('COC request submitted successfully! The lender will review your request and respond within 2 business days.');
  };

  const handleAIAnalysis = (analysis) => {
    setAiAnalysis(analysis);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="empty">
        <div className="empty-icon">
          <i className="ti ti-file-off"></i>
        </div>
        <p className="empty-title">Loan not found</p>
        <p className="empty-subtitle text-secondary">
          The loan you're looking for doesn't exist or has been removed.
        </p>
        <div className="empty-action">
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-2"></i>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="page-header d-print-none"
      style={{
        marginRight: isAIPanelLocked ? '420px' : '0',
        transition: 'margin-right 0.3s ease'
      }}
    >
      {/* Header */}
      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <div className="page-pretitle">
            <button className="btn btn-ghost-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1"></i>
              Back to Pipeline
            </button>
          </div>
          <h2 className="page-title">
            {loan.loanNumber}
            <span className={`badge ${getStatusBadgeClass(loan.status)} ms-2`}>
              {loan.status}
            </span>
            {loan.isRecentlyApproved && (
              <span className="badge bg-green-lt ms-2">
                <i className="ti ti-check me-1"></i>
                Recently Approved
              </span>
            )}
            {isEditMode && (
              <span className="badge bg-yellow-lt ms-2">
                <i className="ti ti-pencil me-1"></i>
                Editing
              </span>
            )}
          </h2>
          <div className="text-secondary mt-1">
            {loan.borrowerName} • {loan.propertyAddress}
          </div>
        </div>
        <div className="col-auto ms-auto">
          {isEditMode ? (
            <div className="btn-list">
              <button className="btn btn-ghost-secondary" onClick={handleEditToggle}>
                <i className="ti ti-x me-1"></i>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleSave}>
                <i className="ti ti-check me-1"></i>
                Save Changes
              </button>
            </div>
          ) : (
            <button 
              className="btn btn-primary" 
              onClick={handleEditToggle}
              disabled={isLoanLocked}
              title={isLoanLocked ? "Cannot edit locked loan" : "Edit loan details"}
            >
              <i className="ti ti-pencil me-1"></i>
              Edit Loan
            </button>
          )}
        </div>
      </div>

      {/* Locked Loan Alert */}
      {isLoanLocked && !isEditMode && (
        <div className="alert alert-warning mb-3">
          <div className="d-flex">
            <div>
              <i className="ti ti-lock me-2"></i>
            </div>
            <div>
              <h4 className="alert-title">Loan is Locked</h4>
              <div className="text-secondary">
                This loan is currently locked with a rate of {loan.loanDetails.lockedRate}% until {new Date(loan.loanDetails.lockExpiration).toLocaleDateString()}.
                Editing is disabled while the loan is locked to preserve rate lock integrity.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {saveSuccess && (
        <div className="alert alert-success alert-dismissible mb-3" role="alert">
          <div className="d-flex">
            <div>
              <i className="ti ti-check me-2"></i>
            </div>
            <div>
              <h4 className="alert-title">Changes Saved Successfully</h4>
              <div className="text-secondary">Your loan updates have been saved.</div>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={() => setSaveSuccess(false)}></button>
        </div>
      )}

      {/* Validation Errors Alert */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="alert alert-danger alert-dismissible mb-3" role="alert">
          <div className="d-flex">
            <div>
              <i className="ti ti-alert-circle me-2"></i>
            </div>
            <div>
              <h4 className="alert-title">Validation Errors</h4>
              <div className="text-secondary">
                Please fix the following errors before saving:
                <ul className="mb-0 mt-2">
                  {Object.values(validationErrors).slice(0, 5).map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                  {Object.keys(validationErrors).length > 5 && (
                    <li>...and {Object.keys(validationErrors).length - 5} more</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={() => setValidationErrors({})}></button>
        </div>
      )}

      {/* Edit Mode Info */}
      {isEditMode && (
        <div className="alert alert-info mb-3">
          <div className="d-flex">
            <div>
              <i className="ti ti-info-circle me-2"></i>
            </div>
            <div>
              <h4 className="alert-title">Edit Mode Active</h4>
              <div className="text-secondary">
                Fields marked with <span className="text-danger">*</span> are required. 
                Make your changes and click "Save Changes" when done.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="row row-deck row-cards mb-3">
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Loan Amount</div>
              </div>
              <div className="h2 mb-0">${loan.loanAmount.toLocaleString()}</div>
              <div className="text-secondary small">{loan.loanDetails.loanProgram}</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Interest Rate</div>
              </div>
              <div className="h2 mb-0">{loan.loanDetails.interestRate}%</div>
              <div className="text-secondary small">APR: {loan.loanDetails.apr}%</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Lock Expiration</div>
              </div>
              {isLoanLocked ? (
                <>
                  <div className={`h3 mb-0 ${isLockExpiringSoon(loan.lockExpiration) ? 'text-yellow' : ''}`}>
                    {new Date(loan.lockExpiration).toLocaleDateString()}
                    {isLockExpiringSoon(loan.lockExpiration) && (
                      <i className="ti ti-alert-circle ms-2"></i>
                    )}
                  </div>
                  <div className="text-secondary small">
                    Locked: {new Date(loan.loanDetails.lockDate).toLocaleDateString()}
                  </div>
                </>
              ) : (
                <>
                  <div className="h3 mb-2 text-muted">Not Locked</div>
                  <button 
                    className="btn btn-sm btn-primary w-100"
                    onClick={() => {/* Placeholder - will implement lock functionality */}}
                    title="Lock interest rate for this loan"
                  >
                    <i className="ti ti-lock me-1"></i>
                    Lock Loan
                  </button>
                  <div className="text-secondary small mt-2">
                    Rate not locked
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Loan Officer</div>
              </div>
              <div className="h3 mb-0">{loan.loanOfficer}</div>
              <div className="text-secondary small">
                Submitted: {new Date(loan.submittedDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs" style={{ flexWrap: 'nowrap' }}>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'overview' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'overview' ? '600' : '400'
                }}
              >
                <i className="ti ti-layout-dashboard me-1"></i>
                Overview
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'borrower' ? 'active' : ''}`}
                onClick={() => setActiveTab('borrower')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'borrower' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'borrower' ? '600' : '400'
                }}
              >
                <i className="ti ti-user me-1"></i>
                Borrower
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'property' ? 'active' : ''}`}
                onClick={() => setActiveTab('property')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'property' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'property' ? '600' : '400'
                }}
              >
                <i className="ti ti-home me-1"></i>
                Property
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'loan' ? 'active' : ''}`}
                onClick={() => setActiveTab('loan')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'loan' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'loan' ? '600' : '400'
                }}
              >
                <i className="ti ti-file-dollar me-1"></i>
                Loan
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'credit' ? 'active' : ''}`}
                onClick={() => setActiveTab('credit')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'credit' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'credit' ? '600' : '400'
                }}
              >
                <i className="ti ti-credit-card me-1"></i>
                Credit / Assets
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'pricing' ? 'active' : ''}`}
                onClick={() => setActiveTab('pricing')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'pricing' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'pricing' ? '600' : '400'
                }}
              >
                <i className="ti ti-calculator me-1"></i>
                Pricing
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('documents')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'documents' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'documents' ? '600' : '400'
                }}
              >
                <i className="ti ti-file-text me-1"></i>
                Docs
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'conditions' ? 'active' : ''}`}
                onClick={() => setActiveTab('conditions')}
                style={{ 
                  cursor: 'pointer',
                  borderBottom: activeTab === 'conditions' ? '3px solid var(--tblr-primary)' : 'none',
                  fontWeight: activeTab === 'conditions' ? '600' : '400'
                }}
              >
                <i className="ti ti-checklist me-1"></i>
                Conditions
              </a>
            </li>
            <li className="nav-item dropdown">
              <a 
                className={`nav-link dropdown-toggle ${['activity', 'messages', 'aus', 'fees', 'coc'].includes(activeTab) ? 'active' : ''}`}
                data-bs-toggle="dropdown"
                data-bs-auto-close="true"
                style={{ cursor: 'pointer' }}
              >
                <i className="ti ti-dots me-1"></i>
                More
              </a>
              <div className="dropdown-menu">
                <a 
                  className={`dropdown-item ${activeTab === 'activity' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activity')}
                  style={{ cursor: 'pointer' }}
                  data-bs-dismiss="dropdown"
                >
                  <i className="ti ti-timeline me-2"></i>
                  Activity
                </a>
                <a 
                  className={`dropdown-item ${activeTab === 'messages' ? 'active' : ''}`}
                  onClick={() => setActiveTab('messages')}
                  style={{ cursor: 'pointer' }}
                  data-bs-dismiss="dropdown"
                >
                  <i className="ti ti-message-circle me-2"></i>
                  Messages
                  {chatThreads.reduce((sum, t) => sum + t.unreadCount, 0) > 0 && (
                    <span className="badge bg-red ms-2">
                      {chatThreads.reduce((sum, t) => sum + t.unreadCount, 0)}
                    </span>
                  )}
                </a>
                <a 
                  className={`dropdown-item ${activeTab === 'aus' ? 'active' : ''}`}
                  onClick={() => setActiveTab('aus')}
                  style={{ cursor: 'pointer' }}
                  data-bs-dismiss="dropdown"
                >
                  <i className="ti ti-robot me-2"></i>
                  AUS (DU + LPA)
                </a>
                <a 
                  className={`dropdown-item ${activeTab === 'fees' ? 'active' : ''}`}
                  onClick={() => setActiveTab('fees')}
                  style={{ cursor: 'pointer' }}
                  data-bs-dismiss="dropdown"
                >
                  <i className="ti ti-receipt me-2"></i>
                  Fees & LE
                </a>
                {isFeatureEnabled('loanCOC') && (
                  <a 
                    className={`dropdown-item ${activeTab === 'coc' ? 'active' : ''}`}
                    onClick={() => setActiveTab('coc')}
                    style={{ cursor: 'pointer' }}
                    data-bs-dismiss="dropdown"
                  >
                    <i className="ti ti-file-alert me-2"></i>
                    COC
                    {cocRequests.filter(r => r.status === 'Submitted' || r.status === 'Under Review').length > 0 && (
                      <span className="badge bg-yellow ms-2">
                        {cocRequests.filter(r => r.status === 'Submitted' || r.status === 'Under Review').length}
                      </span>
                    )}
                  </a>
                )}
              </div>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="row">
              <div className="col-md-6">
                <h3 className="card-title mb-3">Loan Summary</h3>
                {isEditMode ? (
                  <div>
                    <div className="mb-3">
                      <label className="form-label">Loan Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={displayLoan.loanNumber}
                        disabled
                      />
                      <small className="text-muted">Loan number cannot be changed</small>
                    </div>
                    <EditableField
                      label="Loan Type"
                      value={displayLoan.loanDetails.loanType}
                      onChange={(val) => handleFieldChange('loanDetails', 'loanType', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['loanDetails.loanType']}
                      options={[
                        { value: 'Conventional', label: 'Conventional' },
                        { value: 'FHA', label: 'FHA' },
                        { value: 'VA', label: 'VA' },
                        { value: 'USDA', label: 'USDA' },
                        { value: 'Jumbo', label: 'Jumbo' }
                      ]}
                    />
                    <EditableField
                      label="Loan Purpose"
                      value={displayLoan.loanDetails.loanPurpose}
                      onChange={(val) => handleFieldChange('loanDetails', 'loanPurpose', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['loanDetails.loanPurpose']}
                      options={[
                        { value: 'Purchase', label: 'Purchase' },
                        { value: 'Refinance', label: 'Refinance' },
                        { value: 'Cash-Out Refinance', label: 'Cash-Out Refinance' },
                        { value: 'Construction', label: 'Construction' }
                      ]}
                    />
                    <EditableField
                      label="Program"
                      value={displayLoan.loanDetails.loanProgram}
                      onChange={(val) => handleFieldChange('loanDetails', 'loanProgram', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['loanDetails.loanProgram']}
                      options={[
                        { value: '30-Year Fixed', label: '30-Year Fixed' },
                        { value: '20-Year Fixed', label: '20-Year Fixed' },
                        { value: '15-Year Fixed', label: '15-Year Fixed' },
                        { value: '10-Year Fixed', label: '10-Year Fixed' },
                        { value: '5/1 ARM', label: '5/1 ARM' },
                        { value: '7/1 ARM', label: '7/1 ARM' },
                        { value: '10/1 ARM', label: '10/1 ARM' }
                      ]}
                    />
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <div>
                        <span className={`badge ${getStatusBadgeClass(displayLoan.status)}`}>
                          {displayLoan.status}
                        </span>
                      </div>
                      <small className="text-muted">Status is managed by the system</small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Conditions</label>
                      <div>
                        {displayLoan.conditionsCount > 0 ? (
                          <span className="badge bg-yellow-lt">{displayLoan.conditionsCount} Outstanding</span>
                        ) : (
                          <span className="badge bg-green-lt">None</span>
                        )}
                      </div>
                      <small className="text-muted">Conditions are managed separately</small>
                    </div>
                  </div>
                ) : (
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Loan Number</div>
                      <div className="datagrid-content fw-bold">{displayLoan.loanNumber}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Loan Type</div>
                      <div className="datagrid-content">{displayLoan.loanDetails.loanType}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Loan Purpose</div>
                      <div className="datagrid-content">{displayLoan.loanDetails.loanPurpose}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Program</div>
                      <div className="datagrid-content">{displayLoan.loanDetails.loanProgram}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Status</div>
                      <div className="datagrid-content">
                        <span className={`badge ${getStatusBadgeClass(displayLoan.status)}`}>
                          {displayLoan.status}
                        </span>
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Conditions</div>
                      <div className="datagrid-content">
                        {displayLoan.conditionsCount > 0 ? (
                          <span className="badge bg-yellow-lt">{displayLoan.conditionsCount} Outstanding</span>
                        ) : (
                          <span className="badge bg-green-lt">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <h3 className="card-title mb-3">Key Ratios</h3>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Debt-to-Income (DTI)</div>
                    <div className="datagrid-content">
                      <span className={`fw-bold ${loan.ratios.dti > 43 ? 'text-yellow' : 'text-green'}`}>
                        {loan.ratios.dti}%
                      </span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Housing Ratio</div>
                    <div className="datagrid-content fw-bold">{loan.ratios.housingRatio}%</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan-to-Value (LTV)</div>
                    <div className="datagrid-content fw-bold">{loan.ratios.ltv}%</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">CLTV</div>
                    <div className="datagrid-content fw-bold">{loan.ratios.cltv}%</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Monthly Payment</div>
                    <div className="datagrid-content fw-bold text-primary">
                      ${loan.loanDetails.monthlyPayment.toLocaleString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Est. Closing Date</div>
                    <div className="datagrid-content">
                      {new Date(loan.loanDetails.estimatedClosingDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Analysis Section */}
              {aiAnalysis && (
                <div className="col-12 mt-4">
                  <div className="card">
                    <div className="card-header bg-primary-lt">
                      <h3 className="card-title">
                        <i className="ti ti-sparkles me-2"></i>
                        Latest AI Analysis
                      </h3>
                      <div className="card-actions">
                        <span className="text-secondary small">
                          {new Date(aiAnalysis.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-3">
                          <div className="text-center">
                            <div className="display-4 text-primary mb-2">{aiAnalysis.score}</div>
                            <div className="text-secondary">Analysis Score</div>
                          </div>
                        </div>
                        <div className="col-md-9">
                          <h4 className="mb-2">Summary</h4>
                          <p className="text-secondary mb-3">{aiAnalysis.summary}</p>
                          
                          {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
                            <>
                              <h5 className="mb-2">
                                <i className="ti ti-bulb me-1 text-warning"></i>
                                Top Recommendations
                              </h5>
                              <ul className="mb-0">
                                {aiAnalysis.recommendations.slice(0, 3).map((rec, idx) => (
                                  <li key={idx} className="mb-1 text-primary">{rec}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          // Open AI assistant to re-run analysis
                          document.querySelector('.floating-ai-assistant button')?.click();
                        }}
                      >
                        <i className="ti ti-refresh me-1"></i>
                        Re-run Analysis
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Borrower Tab */}
          {activeTab === 'borrower' && (
            <div>
              {/* Self-Service Actions */}
              {isFeatureEnabled('orderVOIEButton') && (
                <div className="mb-3">
                  <button 
                    className="btn btn-success"
                    onClick={() => setShowOrderVOIEModal(true)}
                  >
                    <i className="ti ti-briefcase me-1"></i>
                    Order Digital Income/Employment Verification (VOIE)
                  </button>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <h3 className="card-title mb-3">Primary Borrower</h3>
                {isEditMode ? (
                  <div>
                    <div className="row">
                      <div className="col-md-6">
                        <EditableField
                          label="First Name"
                          value={displayLoan.borrower.firstName}
                          onChange={(val) => handleFieldChange('borrower', 'firstName', val)}
                          isEditMode={isEditMode}
                          required={true}
                          error={validationErrors['borrower.firstName']}
                          placeholder="John"
                        />
                      </div>
                      <div className="col-md-6">
                        <EditableField
                          label="Last Name"
                          value={displayLoan.borrower.lastName}
                          onChange={(val) => handleFieldChange('borrower', 'lastName', val)}
                          isEditMode={isEditMode}
                          required={true}
                          error={validationErrors['borrower.lastName']}
                          placeholder="Smith"
                        />
                      </div>
                    </div>
                    <EditableField
                      label="Email"
                      value={displayLoan.borrower.email}
                      onChange={(val) => handleFieldChange('borrower', 'email', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.email']}
                      type="email"
                      placeholder="john.smith@email.com"
                    />
                    <EditableField
                      label="Phone"
                      value={displayLoan.borrower.phone}
                      onChange={(val) => handleFieldChange('borrower', 'phone', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.phone']}
                      type="tel"
                      placeholder="(555) 123-4567"
                    />
                    <EditableField
                      label="SSN"
                      value={displayLoan.borrower.ssn}
                      onChange={(val) => handleFieldChange('borrower', 'ssn', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.ssn']}
                      placeholder="***-**-1234"
                    />
                    <EditableField
                      label="Date of Birth"
                      value={displayLoan.borrower.dateOfBirth}
                      onChange={(val) => handleFieldChange('borrower', 'dateOfBirth', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.dateOfBirth']}
                      type="date"
                    />
                    <EditableField
                      label="Marital Status"
                      value={displayLoan.borrower.maritalStatus}
                      onChange={(val) => handleFieldChange('borrower', 'maritalStatus', val)}
                      isEditMode={isEditMode}
                      options={[
                        { value: 'Single', label: 'Single' },
                        { value: 'Married', label: 'Married' },
                        { value: 'Divorced', label: 'Divorced' },
                        { value: 'Widowed', label: 'Widowed' }
                      ]}
                    />
                    <EditableField
                      label="Dependents"
                      value={displayLoan.borrower.dependents}
                      onChange={(val) => handleFieldChange('borrower', 'dependents', parseInt(val) || 0)}
                      isEditMode={isEditMode}
                      type="number"
                      placeholder="0"
                    />
                    <EditableField
                      label="Credit Score"
                      value={displayLoan.borrower.creditScore}
                      onChange={(val) => handleFieldChange('borrower', 'creditScore', parseInt(val) || 0)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.creditScore']}
                      type="number"
                      placeholder="300-850"
                    />
                  </div>
                ) : (
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Name</div>
                      <div className="datagrid-content fw-bold">
                        {displayLoan.borrower.firstName} {displayLoan.borrower.lastName}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Email</div>
                      <div className="datagrid-content">{displayLoan.borrower.email}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Phone</div>
                      <div className="datagrid-content">{displayLoan.borrower.phone}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">SSN</div>
                      <div className="datagrid-content">{displayLoan.borrower.ssn}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Date of Birth</div>
                      <div className="datagrid-content">
                        {new Date(displayLoan.borrower.dateOfBirth).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Marital Status</div>
                      <div className="datagrid-content">{displayLoan.borrower.maritalStatus}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Dependents</div>
                      <div className="datagrid-content">{displayLoan.borrower.dependents}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Credit Score</div>
                      <div className="datagrid-content">
                        <span className="badge bg-blue-lt fs-3">{displayLoan.borrower.creditScore}</span>
                      </div>
                    </div>
                  </div>
                )}

                <h4 className="card-subtitle mt-4 mb-3">Employment</h4>
                {isEditMode ? (
                  <div>
                    <EditableField
                      label="Employer"
                      value={displayLoan.borrower.employment.employer}
                      onChange={(val) => handleFieldChange('borrower', 'employment', val, 'employer')}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.employment.employer']}
                      placeholder="Company Name"
                    />
                    <EditableField
                      label="Position"
                      value={displayLoan.borrower.employment.position}
                      onChange={(val) => handleFieldChange('borrower', 'employment', val, 'position')}
                      isEditMode={isEditMode}
                      placeholder="Job Title"
                    />
                    <EditableField
                      label="Years Employed"
                      value={displayLoan.borrower.employment.yearsEmployed}
                      onChange={(val) => handleFieldChange('borrower', 'employment', parseFloat(val) || 0, 'yearsEmployed')}
                      isEditMode={isEditMode}
                      type="number"
                      placeholder="5.5"
                    />
                    <EditableField
                      label="Monthly Income"
                      value={displayLoan.borrower.employment.monthlyIncome}
                      onChange={(val) => handleFieldChange('borrower', 'employment', parseFloat(val) || 0, 'monthlyIncome')}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['borrower.employment.monthlyIncome']}
                      type="number"
                      placeholder="10000"
                    />
                    <EditableField
                      label="Employment Type"
                      value={displayLoan.borrower.employment.employmentType}
                      onChange={(val) => handleFieldChange('borrower', 'employment', val, 'employmentType')}
                      isEditMode={isEditMode}
                      options={[
                        { value: 'W2', label: 'W2 Employee' },
                        { value: 'Self-Employed', label: 'Self-Employed' },
                        { value: '1099', label: '1099 Contractor' },
                        { value: 'Retired', label: 'Retired' }
                      ]}
                    />
                  </div>
                ) : (
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Employer</div>
                      <div className="datagrid-content">{displayLoan.borrower.employment.employer}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Position</div>
                      <div className="datagrid-content">{displayLoan.borrower.employment.position}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Years Employed</div>
                      <div className="datagrid-content">{displayLoan.borrower.employment.yearsEmployed} years</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Monthly Income</div>
                      <div className="datagrid-content fw-bold text-success">
                        ${displayLoan.borrower.employment.monthlyIncome.toLocaleString()}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Type</div>
                      <div className="datagrid-content">{displayLoan.borrower.employment.employmentType}</div>
                    </div>
                  </div>
                )}
              </div>

              {displayLoan.coBorrower && (
                <div className="col-md-6">
                  <h3 className="card-title mb-3">Co-Borrower</h3>
                  {isEditMode ? (
                    <div>
                      <div className="row">
                        <div className="col-md-6">
                          <EditableField
                            label="First Name"
                            value={displayLoan.coBorrower.firstName}
                            onChange={(val) => handleFieldChange('coBorrower', 'firstName', val)}
                            isEditMode={isEditMode}
                            placeholder="Jane"
                          />
                        </div>
                        <div className="col-md-6">
                          <EditableField
                            label="Last Name"
                            value={displayLoan.coBorrower.lastName}
                            onChange={(val) => handleFieldChange('coBorrower', 'lastName', val)}
                            isEditMode={isEditMode}
                            placeholder="Smith"
                          />
                        </div>
                      </div>
                      <EditableField
                        label="Email"
                        value={displayLoan.coBorrower.email}
                        onChange={(val) => handleFieldChange('coBorrower', 'email', val)}
                        isEditMode={isEditMode}
                        type="email"
                        placeholder="jane.smith@email.com"
                      />
                      <EditableField
                        label="Phone"
                        value={displayLoan.coBorrower.phone}
                        onChange={(val) => handleFieldChange('coBorrower', 'phone', val)}
                        isEditMode={isEditMode}
                        type="tel"
                        placeholder="(555) 123-4567"
                      />
                      <EditableField
                        label="SSN"
                        value={displayLoan.coBorrower.ssn}
                        onChange={(val) => handleFieldChange('coBorrower', 'ssn', val)}
                        isEditMode={isEditMode}
                        placeholder="***-**-5678"
                      />
                      <EditableField
                        label="Date of Birth"
                        value={displayLoan.coBorrower.dateOfBirth}
                        onChange={(val) => handleFieldChange('coBorrower', 'dateOfBirth', val)}
                        isEditMode={isEditMode}
                        type="date"
                      />
                      <EditableField
                        label="Credit Score"
                        value={displayLoan.coBorrower.creditScore}
                        onChange={(val) => handleFieldChange('coBorrower', 'creditScore', parseInt(val) || 0)}
                        isEditMode={isEditMode}
                        type="number"
                        placeholder="300-850"
                      />
                    </div>
                  ) : (
                    <div className="datagrid">
                      <div className="datagrid-item">
                        <div className="datagrid-title">Name</div>
                        <div className="datagrid-content fw-bold">
                          {displayLoan.coBorrower.firstName} {displayLoan.coBorrower.lastName}
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Email</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.email}</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Phone</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.phone}</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">SSN</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.ssn}</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Date of Birth</div>
                        <div className="datagrid-content">
                          {new Date(displayLoan.coBorrower.dateOfBirth).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Credit Score</div>
                        <div className="datagrid-content">
                          <span className="badge bg-blue-lt fs-3">{displayLoan.coBorrower.creditScore}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <h4 className="card-subtitle mt-4 mb-3">Employment</h4>
                  {isEditMode ? (
                    <div>
                      <EditableField
                        label="Employer"
                        value={displayLoan.coBorrower.employment.employer}
                        onChange={(val) => handleFieldChange('coBorrower', 'employment', val, 'employer')}
                        isEditMode={isEditMode}
                        placeholder="Company Name"
                      />
                      <EditableField
                        label="Position"
                        value={displayLoan.coBorrower.employment.position}
                        onChange={(val) => handleFieldChange('coBorrower', 'employment', val, 'position')}
                        isEditMode={isEditMode}
                        placeholder="Job Title"
                      />
                      <EditableField
                        label="Years Employed"
                        value={displayLoan.coBorrower.employment.yearsEmployed}
                        onChange={(val) => handleFieldChange('coBorrower', 'employment', parseFloat(val) || 0, 'yearsEmployed')}
                        isEditMode={isEditMode}
                        type="number"
                        placeholder="5.5"
                      />
                      <EditableField
                        label="Monthly Income"
                        value={displayLoan.coBorrower.employment.monthlyIncome}
                        onChange={(val) => handleFieldChange('coBorrower', 'employment', parseFloat(val) || 0, 'monthlyIncome')}
                        isEditMode={isEditMode}
                        type="number"
                        placeholder="10000"
                      />
                      <EditableField
                        label="Employment Type"
                        value={displayLoan.coBorrower.employment.employmentType}
                        onChange={(val) => handleFieldChange('coBorrower', 'employment', val, 'employmentType')}
                        isEditMode={isEditMode}
                        options={[
                          { value: 'W2', label: 'W2 Employee' },
                          { value: 'Self-Employed', label: 'Self-Employed' },
                          { value: '1099', label: '1099 Contractor' },
                          { value: 'Retired', label: 'Retired' }
                        ]}
                      />
                    </div>
                  ) : (
                    <div className="datagrid">
                      <div className="datagrid-item">
                        <div className="datagrid-title">Employer</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.employment.employer}</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Position</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.employment.position}</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Years Employed</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.employment.yearsEmployed} years</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Monthly Income</div>
                        <div className="datagrid-content fw-bold text-success">
                          ${displayLoan.coBorrower.employment.monthlyIncome.toLocaleString()}
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Type</div>
                        <div className="datagrid-content">{displayLoan.coBorrower.employment.employmentType}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
          )}

          {/* Property Tab */}
          {activeTab === 'property' && (
            <div>
              {/* Self-Service Actions */}
              {(isFeatureEnabled('orderAppraisalButton') || isFeatureEnabled('openTitleButton')) && (
                <div className="mb-3">
                  {isFeatureEnabled('orderAppraisalButton') && (
                    <button 
                      className="btn btn-primary me-2"
                      onClick={() => setShowOrderAppraisalModal(true)}
                    >
                      <i className="ti ti-home-search me-1"></i>
                      Order Appraisal
                    </button>
                  )}
                  {isFeatureEnabled('openTitleButton') && (
                    <button 
                      className="btn btn-success"
                      onClick={() => setShowOpenTitleModal(true)}
                    >
                      <i className="ti ti-file-certificate me-1"></i>
                      Open Title Order
                    </button>
                  )}
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <h3 className="card-title mb-3">Property Information</h3>
                {isEditMode ? (
                  <div>
                    <EditableField
                      label="Address"
                      value={displayLoan.property.address}
                      onChange={(val) => handleFieldChange('property', 'address', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['property.address']}
                      placeholder="123 Main St"
                    />
                    <div className="row">
                      <div className="col-md-6">
                        <EditableField
                          label="City"
                          value={displayLoan.property.city}
                          onChange={(val) => handleFieldChange('property', 'city', val)}
                          isEditMode={isEditMode}
                          required={true}
                          error={validationErrors['property.city']}
                          placeholder="Los Angeles"
                        />
                      </div>
                      <div className="col-md-6">
                        <EditableField
                          label="State"
                          value={displayLoan.property.state}
                          onChange={(val) => handleFieldChange('property', 'state', val)}
                          isEditMode={isEditMode}
                          required={true}
                          error={validationErrors['property.state']}
                          placeholder="CA"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <EditableField
                          label="ZIP Code"
                          value={displayLoan.property.zip}
                          onChange={(val) => handleFieldChange('property', 'zip', val)}
                          isEditMode={isEditMode}
                          required={true}
                          error={validationErrors['property.zip']}
                          placeholder="90001"
                        />
                      </div>
                      <div className="col-md-6">
                        <EditableField
                          label="County"
                          value={displayLoan.property.county}
                          onChange={(val) => handleFieldChange('property', 'county', val)}
                          isEditMode={isEditMode}
                          placeholder="Los Angeles"
                        />
                      </div>
                    </div>
                    <EditableField
                      label="Property Type"
                      value={displayLoan.property.propertyType}
                      onChange={(val) => handleFieldChange('property', 'propertyType', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['property.propertyType']}
                      options={[
                        { value: 'Single Family', label: 'Single Family' },
                        { value: 'Condominium', label: 'Condominium' },
                        { value: 'Townhouse', label: 'Townhouse' },
                        { value: 'Multi-Family', label: 'Multi-Family' },
                        { value: 'Manufactured', label: 'Manufactured' }
                      ]}
                    />
                    <EditableField
                      label="Occupancy"
                      value={displayLoan.property.occupancy}
                      onChange={(val) => handleFieldChange('property', 'occupancy', val)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['property.occupancy']}
                      options={[
                        { value: 'Primary Residence', label: 'Primary Residence' },
                        { value: 'Second Home', label: 'Second Home' },
                        { value: 'Investment Property', label: 'Investment Property' }
                      ]}
                    />
                    <div className="row">
                      <div className="col-md-6">
                        <EditableField
                          label="Units"
                          value={displayLoan.property.units}
                          onChange={(val) => handleFieldChange('property', 'units', parseInt(val) || 1)}
                          isEditMode={isEditMode}
                          type="number"
                          placeholder="1"
                        />
                      </div>
                      <div className="col-md-6">
                        <EditableField
                          label="Year Built"
                          value={displayLoan.property.yearBuilt}
                          onChange={(val) => handleFieldChange('property', 'yearBuilt', parseInt(val) || 2000)}
                          isEditMode={isEditMode}
                          type="number"
                          placeholder="2020"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Address</div>
                      <div className="datagrid-content fw-bold">{displayLoan.property.address}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">City</div>
                      <div className="datagrid-content">{displayLoan.property.city}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">State</div>
                      <div className="datagrid-content">{displayLoan.property.state}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">ZIP Code</div>
                      <div className="datagrid-content">{displayLoan.property.zip}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">County</div>
                      <div className="datagrid-content">{displayLoan.property.county}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Property Type</div>
                      <div className="datagrid-content">
                        <span className="badge bg-azure-lt">{displayLoan.property.propertyType}</span>
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Occupancy</div>
                      <div className="datagrid-content">{displayLoan.property.occupancy}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Units</div>
                      <div className="datagrid-content">{displayLoan.property.units}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Year Built</div>
                      <div className="datagrid-content">{displayLoan.property.yearBuilt}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <h3 className="card-title mb-3">Valuation</h3>
                {isEditMode ? (
                  <div>
                    <EditableField
                      label="Purchase Price"
                      value={displayLoan.property.purchasePrice}
                      onChange={(val) => handleFieldChange('property', 'purchasePrice', parseFloat(val) || 0)}
                      isEditMode={isEditMode}
                      required={true}
                      error={validationErrors['property.purchasePrice']}
                      type="number"
                      placeholder="500000"
                    />
                    <EditableField
                      label="Appraised Value"
                      value={displayLoan.property.appraisedValue}
                      onChange={(val) => handleFieldChange('property', 'appraisedValue', parseFloat(val) || 0)}
                      isEditMode={isEditMode}
                      type="number"
                      placeholder="500000"
                    />
                    <EditableField
                      label="Estimated Value"
                      value={displayLoan.property.estimatedValue}
                      onChange={(val) => handleFieldChange('property', 'estimatedValue', parseFloat(val) || 0)}
                      isEditMode={isEditMode}
                      type="number"
                      placeholder="500000"
                    />
                    <div className="alert alert-info">
                      <i className="ti ti-info-circle me-2"></i>
                      <small>Down payment and LTV are calculated automatically based on purchase price and loan amount.</small>
                    </div>
                  </div>
                ) : (
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Purchase Price</div>
                      <div className="datagrid-content fw-bold text-primary">
                        ${displayLoan.property.purchasePrice.toLocaleString()}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Appraised Value</div>
                      <div className="datagrid-content fw-bold text-success">
                        ${displayLoan.property.appraisedValue.toLocaleString()}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Estimated Value</div>
                      <div className="datagrid-content">
                        ${displayLoan.property.estimatedValue.toLocaleString()}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Down Payment</div>
                      <div className="datagrid-content">
                        ${displayLoan.loanDetails.downPayment.toLocaleString()} 
                        <span className="text-secondary ms-2">
                          ({displayLoan.loanDetails.downPaymentPercent.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Loan Amount</div>
                      <div className="datagrid-content fw-bold">
                        ${displayLoan.loanDetails.loanAmount.toLocaleString()}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">LTV Ratio</div>
                      <div className="datagrid-content">
                        <span className={`badge ${displayLoan.ratios.ltv > 80 ? 'bg-yellow-lt' : 'bg-green-lt'}`}>
                          {displayLoan.ratios.ltv}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          )}

          {/* Loan Details Tab */}
          {activeTab === 'loan' && (
            <div className="row">
              <div className="col-md-6">
                <h3 className="card-title mb-3">Loan Terms</h3>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan Amount</div>
                    <div className="datagrid-content fw-bold text-primary">
                      ${loan.loanDetails.loanAmount.toLocaleString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Interest Rate</div>
                    <div className="datagrid-content fw-bold">{loan.loanDetails.interestRate}%</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">APR</div>
                    <div className="datagrid-content">{loan.loanDetails.apr}%</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Term</div>
                    <div className="datagrid-content">{loan.loanDetails.term} months ({loan.loanDetails.term / 12} years)</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Monthly Payment</div>
                    <div className="datagrid-content fw-bold text-success">
                      ${loan.loanDetails.monthlyPayment.toLocaleString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan Program</div>
                    <div className="datagrid-content">
                      <span className="badge bg-blue-lt">{loan.loanDetails.loanProgram}</span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan Type</div>
                    <div className="datagrid-content">{loan.loanDetails.loanType}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan Purpose</div>
                    <div className="datagrid-content">{loan.loanDetails.loanPurpose}</div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <h3 className="card-title mb-3">Lock Information</h3>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Lock Date</div>
                    <div className="datagrid-content">
                      {new Date(loan.loanDetails.lockDate).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Lock Expiration</div>
                    <div className="datagrid-content">
                      <span className={isLockExpiringSoon(loan.lockExpiration) ? 'text-yellow fw-bold' : ''}>
                        {new Date(loan.lockExpiration).toLocaleDateString()}
                        {isLockExpiringSoon(loan.lockExpiration) && (
                          <i className="ti ti-alert-circle ms-2"></i>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Locked Rate</div>
                    <div className="datagrid-content fw-bold">{loan.loanDetails.lockedRate}%</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Submitted Date</div>
                    <div className="datagrid-content">
                      {new Date(loan.submittedDate).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Est. Closing Date</div>
                    <div className="datagrid-content fw-bold">
                      {new Date(loan.loanDetails.estimatedClosingDate).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan Officer</div>
                    <div className="datagrid-content">{loan.loanOfficer}</div>
                  </div>
                </div>

                {isLockExpiringSoon(loan.lockExpiration) && (
                  <div className="alert alert-warning mt-3">
                    <div className="d-flex">
                      <div>
                        <i className="ti ti-alert-triangle me-2"></i>
                      </div>
                      <div>
                        <h4 className="alert-title">Lock Expiring Soon</h4>
                        <div className="text-secondary">
                          This loan's rate lock expires on {new Date(loan.lockExpiration).toLocaleDateString()}. 
                          Please ensure closing occurs before this date or consider extending the lock.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Credit / Assets Tab */}
          {activeTab === 'credit' && (
            <div>
              {/* Self-Service Actions */}
              {(isFeatureEnabled('pullCreditButton') || isFeatureEnabled('orderVOAButton')) && (
                <div className="mb-3">
                  {isFeatureEnabled('pullCreditButton') && (
                    <button 
                      className="btn btn-primary me-2"
                      onClick={() => setShowPullCreditModal(true)}
                    >
                      <i className="ti ti-download me-1"></i>
                      Pull Credit Report
                    </button>
                  )}
                  {isFeatureEnabled('orderVOAButton') && (
                    <button 
                      className="btn btn-success"
                      onClick={() => setShowOrderVOAModal(true)}
                    >
                      <i className="ti ti-building-bank me-1"></i>
                      Order Digital Asset Verification (VOA)
                    </button>
                  )}
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <h3 className="card-title mb-3">Credit Profile</h3>
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Credit Score</div>
                      <div className="datagrid-content fw-bold text-primary">
                        {loan.borrower.creditScore}
                      </div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Total Monthly Debt</div>
                      <div className="datagrid-content">${loan.liabilities.totalMonthlyPayment?.toLocaleString() || '2,850'}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Debt-to-Income Ratio</div>
                      <div className="datagrid-content fw-bold">
                        {((loan.liabilities.totalMonthlyPayment || 2850) / loan.borrower.employment.monthlyIncome * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  <h3 className="card-title mt-4 mb-3">Assets</h3>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Checking Accounts</div>
                    <div className="datagrid-content">${loan.assets.checkingAccounts.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Savings Accounts</div>
                    <div className="datagrid-content">${loan.assets.savingsAccounts.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Retirement Accounts</div>
                    <div className="datagrid-content">${loan.assets.retirement.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Other Assets</div>
                    <div className="datagrid-content">${loan.assets.other.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Total Assets</div>
                    <div className="datagrid-content fw-bold text-success">
                      ${loan.assets.total.toLocaleString()}
                    </div>
                  </div>
                </div>

                <h3 className="card-title mt-4 mb-3">Liabilities</h3>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Credit Cards</div>
                    <div className="datagrid-content">${loan.liabilities.creditCards.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Auto Loans</div>
                    <div className="datagrid-content">${loan.liabilities.autoLoans.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Student Loans</div>
                    <div className="datagrid-content">${loan.liabilities.studentLoans.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Other Liabilities</div>
                    <div className="datagrid-content">${loan.liabilities.other.toLocaleString()}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Total Liabilities</div>
                    <div className="datagrid-content fw-bold text-danger">
                      ${loan.liabilities.total.toLocaleString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Monthly Debt Payments</div>
                    <div className="datagrid-content fw-bold">
                      ${loan.liabilities.monthlyDebts.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <h3 className="card-title mb-3">Income & Ratios</h3>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Primary Borrower Income</div>
                    <div className="datagrid-content fw-bold">
                      ${loan.borrower.employment.monthlyIncome.toLocaleString()}/mo
                    </div>
                  </div>
                  {loan.coBorrower && (
                    <div className="datagrid-item">
                      <div className="datagrid-title">Co-Borrower Income</div>
                      <div className="datagrid-content fw-bold">
                        ${loan.coBorrower.employment.monthlyIncome.toLocaleString()}/mo
                      </div>
                    </div>
                  )}
                  <div className="datagrid-item">
                    <div className="datagrid-title">Total Monthly Income</div>
                    <div className="datagrid-content fw-bold text-success">
                      ${(loan.borrower.employment.monthlyIncome + (loan.coBorrower?.employment.monthlyIncome || 0)).toLocaleString()}/mo
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Monthly Debts</div>
                    <div className="datagrid-content">
                      ${loan.liabilities.monthlyDebts.toLocaleString()}/mo
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Proposed Housing Payment</div>
                    <div className="datagrid-content">
                      ${loan.loanDetails.monthlyPayment.toLocaleString()}/mo
                    </div>
                  </div>
                </div>

                <h4 className="card-subtitle mt-4 mb-3">Debt Ratios</h4>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Housing Ratio</div>
                    <div className="datagrid-content">
                      <span className={`badge ${loan.ratios.housingRatio > 28 ? 'bg-yellow-lt' : 'bg-green-lt'} fs-3`}>
                        {loan.ratios.housingRatio}%
                      </span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Debt-to-Income (DTI)</div>
                    <div className="datagrid-content">
                      <span className={`badge ${loan.ratios.dti > 43 ? 'bg-red-lt' : loan.ratios.dti > 36 ? 'bg-yellow-lt' : 'bg-green-lt'} fs-3`}>
                        {loan.ratios.dti}%
                      </span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Loan-to-Value (LTV)</div>
                    <div className="datagrid-content">
                      <span className={`badge ${loan.ratios.ltv > 80 ? 'bg-yellow-lt' : 'bg-green-lt'} fs-3`}>
                        {loan.ratios.ltv}%
                      </span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">CLTV</div>
                    <div className="datagrid-content">
                      <span className="badge bg-blue-lt fs-3">{loan.ratios.cltv}%</span>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info mt-3">
                  <div className="d-flex">
                    <div>
                      <i className="ti ti-info-circle me-2"></i>
                    </div>
                    <div>
                      <h4 className="alert-title">Ratio Guidelines</h4>
                      <div className="text-secondary small">
                        <strong>Housing Ratio:</strong> Typically ≤28% for conventional loans
                        <br />
                        <strong>DTI:</strong> Typically ≤43% for qualified mortgages
                        <br />
                        <strong>LTV:</strong> Varies by loan type; &gt;80% typically requires PMI
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">Pricing Scenarios ({pricingScenarios.length})</h3>
                <div className="btn-list">
                  {isFeatureEnabled('runPricingButton') && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowRunPricingModal(true)}
                    >
                      <i className="ti ti-calculator me-1"></i>
                      Run Pricing
                    </button>
                  )}
                  {selectedScenarios.length >= 2 && (
                    <button 
                      className="btn btn-outline-primary"
                      onClick={() => setShowCompareModal(true)}
                    >
                      <i className="ti ti-arrows-left-right me-1"></i>
                      Compare ({selectedScenarios.length})
                    </button>
                  )}
                </div>
              </div>

              {/* Current Rate Sheet Info */}
              <div className="alert alert-info mb-3">
                <div className="d-flex">
                  <div><i className="ti ti-info-circle me-2"></i></div>
                  <div>
                    <strong>Current Rate Sheet</strong>
                    <div className="text-secondary small">
                      Effective: {new Date(mockRateSheet.effectiveDate).toLocaleString()} | 
                      Expires: {new Date(mockRateSheet.expirationDate).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Scenarios Table */}
              {pricingScenarios.length === 0 ? (
                <div className="empty">
                  <div className="empty-icon">
                    <i className="ti ti-calculator"></i>
                  </div>
                  <p className="empty-title">No pricing scenarios</p>
                  <p className="empty-subtitle text-secondary">
                    Run pricing to see available loan programs and rates
                  </p>
                  {isFeatureEnabled('runPricingButton') && (
                    <div className="empty-action">
                      <button className="btn btn-primary" onClick={() => setShowRunPricingModal(true)}>
                        <i className="ti ti-calculator me-1"></i>
                        Run First Pricing
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>
                          <input 
                            type="checkbox" 
                            className="form-check-input"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedScenarios(pricingScenarios.map(s => s.id));
                              } else {
                                setSelectedScenarios([]);
                              }
                            }}
                          />
                        </th>
                        <th>Scenario</th>
                        <th>Program</th>
                        <th>Rate</th>
                        <th>APR</th>
                        <th>Payment</th>
                        <th>Points</th>
                        <th>Total Interest</th>
                        <th>Status</th>
                        <th className="w-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricingScenarios.map(scenario => (
                        <tr key={scenario.id} className={scenario.isSelected ? 'table-active' : ''}>
                          <td>
                            <input 
                              type="checkbox" 
                              className="form-check-input"
                              checked={selectedScenarios.includes(scenario.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedScenarios([...selectedScenarios, scenario.id]);
                                } else {
                                  setSelectedScenarios(selectedScenarios.filter(id => id !== scenario.id));
                                }
                              }}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {scenario.isSelected && (
                                <i className="ti ti-star-filled text-yellow me-2"></i>
                              )}
                              <div>
                                <div className="fw-bold">{scenario.scenarioName}</div>
                                <div className="text-secondary small">
                                  Created by {scenario.createdBy} on {new Date(scenario.createdDate).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-blue-lt">{scenario.programName}</span>
                          </td>
                          <td className="fw-bold">{scenario.interestRate.toFixed(3)}%</td>
                          <td>{scenario.apr.toFixed(3)}%</td>
                          <td className="fw-bold text-primary">
                            ${scenario.monthlyPayment.toLocaleString()}/mo
                          </td>
                          <td>
                            {scenario.points > 0 ? (
                              <span className="text-red">{scenario.points.toFixed(3)}</span>
                            ) : scenario.points < 0 ? (
                              <span className="text-green">{scenario.points.toFixed(3)}</span>
                            ) : (
                              <span>0.000</span>
                            )}
                          </td>
                          <td className="text-secondary">
                            ${scenario.totalInterest.toLocaleString()}
                          </td>
                          <td>
                            {scenario.pricingDetails?.lockPeriod ? (
                              <span className="badge bg-green">
                                Locked {scenario.pricingDetails.lockPeriod}d
                              </span>
                            ) : (
                              <span className="badge bg-gray-lt">Not Locked</span>
                            )}
                          </td>
                          <td>
                            <div className="btn-list">
                              <button 
                                className="btn btn-sm btn-ghost-primary"
                                onClick={() => {
                                  setSelectedScenario(scenario);
                                  setShowRateStackModal(true);
                                }}
                                title="View Rate Stack"
                              >
                                <i className="ti ti-eye"></i>
                              </button>
                              {!scenario.pricingDetails?.lockPeriod && (
                                <button 
                                  className="btn btn-sm btn-primary"
                                  onClick={() => {
                                    setSelectedScenario(scenario);
                                    setShowLockRateModal(true);
                                  }}
                                >
                                  <i className="ti ti-lock me-1"></i>
                                  Lock
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Scenario Details Cards */}
              {pricingScenarios.length > 0 && (
                <div className="row mt-4">
                  {pricingScenarios.filter(s => s.isSelected).map(scenario => (
                    <div key={scenario.id} className="col-12 mb-3">
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title">
                            <i className="ti ti-star-filled text-yellow me-2"></i>
                            {scenario.scenarioName} - Selected Program
                          </h3>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <h4 className="mb-3">Rate Details</h4>
                              <div className="datagrid">
                                <div className="datagrid-item">
                                  <div className="datagrid-title">Base Rate</div>
                                  <div className="datagrid-content fw-bold">
                                    {scenario.pricingDetails.baseRate.toFixed(3)}%
                                  </div>
                                </div>
                                <div className="datagrid-item">
                                  <div className="datagrid-title">Final Rate</div>
                                  <div className="datagrid-content fw-bold text-primary">
                                    {scenario.pricingDetails.finalRate.toFixed(3)}%
                                  </div>
                                </div>
                                <div className="datagrid-item">
                                  <div className="datagrid-title">APR</div>
                                  <div className="datagrid-content">
                                    {scenario.apr.toFixed(3)}%
                                  </div>
                                </div>
                                <div className="datagrid-item">
                                  <div className="datagrid-title">Monthly Payment</div>
                                  <div className="datagrid-content fw-bold text-primary">
                                    ${scenario.monthlyPayment.toLocaleString()}
                                  </div>
                                </div>
                              </div>

                              <h4 className="mt-4 mb-3">Rate Adjustments</h4>
                              <div className="list-group list-group-flush">
                                {scenario.pricingDetails.adjustments.map((adj, idx) => (
                                  <div key={idx} className="list-group-item px-0">
                                    <div className="row align-items-center">
                                      <div className="col">{adj.description}</div>
                                      <div className="col-auto">
                                        <span className={`badge ${
                                          adj.adjustment > 0 ? 'bg-red-lt' :
                                          adj.adjustment < 0 ? 'bg-green-lt' : 'bg-gray-lt'
                                        }`}>
                                          {adj.adjustment > 0 ? '+' : ''}{adj.adjustment.toFixed(3)}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <h4 className="mb-3">Cost Summary</h4>
                              <div className="datagrid">
                                <div className="datagrid-item">
                                  <div className="datagrid-title">Loan Amount</div>
                                  <div className="datagrid-content fw-bold">
                                    ${scenario.loanAmount.toLocaleString()}
                                  </div>
                                </div>
                                <div className="datagrid-item">
                                  <div className="datagrid-title">Closing Costs</div>
                                  <div className="datagrid-content">
                                    ${scenario.closingCosts.toLocaleString()}
                                  </div>
                                </div>
                                {scenario.points !== 0 && (
                                  <div className="datagrid-item">
                                    <div className="datagrid-title">
                                      {scenario.points > 0 ? 'Discount Points' : 'Lender Credits'}
                                    </div>
                                    <div className="datagrid-content">
                                      {scenario.points > 0 ? (
                                        <span className="text-red">
                                          ${scenario.pointsCost?.toLocaleString() || 0}
                                        </span>
                                      ) : (
                                        <span className="text-green">
                                          -${Math.abs(scenario.credits || 0).toLocaleString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {scenario.pmi > 0 && (
                                  <div className="datagrid-item">
                                    <div className="datagrid-title">PMI (Monthly)</div>
                                    <div className="datagrid-content">
                                      ${scenario.pmi.toLocaleString()}
                                    </div>
                                  </div>
                                )}
                                <div className="datagrid-item">
                                  <div className="datagrid-title">Total Interest</div>
                                  <div className="datagrid-content text-secondary">
                                    ${scenario.totalInterest.toLocaleString()}
                                  </div>
                                </div>
                              </div>

                              {scenario.pricingDetails.lockPeriod && (
                                <div className="alert alert-success mt-3">
                                  <div className="d-flex">
                                    <div><i className="ti ti-lock me-2"></i></div>
                                    <div>
                                      <strong>Rate Locked</strong>
                                      <div className="text-secondary small">
                                        {scenario.pricingDetails.lockPeriod} days until {new Date(scenario.pricingDetails.expirationDate).toLocaleDateString()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {scenario.pricingDetails.armDetails && (
                                <div className="alert alert-info mt-3">
                                  <strong>ARM Details</strong>
                                  <div className="text-secondary small">
                                    Fixed for {scenario.pricingDetails.armDetails.initialFixedPeriod} months, 
                                    then adjusts based on {scenario.pricingDetails.armDetails.index} + {scenario.pricingDetails.armDetails.margin}%
                                    <br />
                                    Caps: Initial {scenario.pricingDetails.armDetails.initialCap}%, 
                                    Periodic {scenario.pricingDetails.armDetails.periodicCap}%, 
                                    Lifetime {scenario.pricingDetails.armDetails.lifetimeCap}%
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Lock History */}
              {lockHistory.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-3">Lock History</h4>
                  <div className="table-responsive">
                    <table className="table table-vcenter">
                      <thead>
                        <tr>
                          <th>Lock Date</th>
                          <th>Rate</th>
                          <th>Period</th>
                          <th>Expiration</th>
                          <th>Locked By</th>
                          <th>Confirmation</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lockHistory.map(lock => (
                          <tr key={lock.id}>
                            <td>{new Date(lock.lockDate).toLocaleString()}</td>
                            <td className="fw-bold">{lock.lockedRate.toFixed(3)}%</td>
                            <td>{lock.lockPeriod} days</td>
                            <td>{new Date(lock.expirationDate).toLocaleDateString()}</td>
                            <td>{lock.lockedBy}</td>
                            <td className="font-monospace small">{lock.lockConfirmation}</td>
                            <td>
                              <span className={`badge ${
                                lock.status === 'active' ? 'bg-green' :
                                lock.status === 'expired' ? 'bg-red' : 'bg-gray'
                              }`}>
                                {lock.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">Documents ({documents.length})</h3>
                <div className="btn-list">
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <i className="ti ti-upload me-1"></i>
                    Upload Document
                  </button>
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => setShowBorrowerLinkModal(true)}
                  >
                    <i className="ti ti-link me-1"></i>
                    Generate Borrower Link
                  </button>
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => setShowESignModal(true)}
                  >
                    <i className="ti ti-signature me-1"></i>
                    Send for eSign
                  </button>
                </div>
              </div>

              {documents.length === 0 ? (
                <div className="empty">
                  <div className="empty-icon">
                    <i className="ti ti-file-text"></i>
                  </div>
                  <p className="empty-title">No documents uploaded</p>
                  <p className="empty-subtitle text-secondary">
                    Upload documents or generate a borrower link to get started
                  </p>
                  <div className="empty-action">
                    <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
                      <i className="ti ti-upload me-1"></i>
                      Upload First Document
                    </button>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Type</th>
                        <th>Uploaded By</th>
                        <th>Date</th>
                        <th>Condition</th>
                        <th>Status</th>
                        <th>AI Flags</th>
                        <th className="w-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map(doc => {
                        const docType = documentTypes.find(t => t.value === doc.documentType);
                        const linkedCondition = conditions.find(c => c.id === doc.conditionId);
                        
                        return (
                          <tr key={doc.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className={`ti ti-file-text me-2 ${doc.eSignStatus ? 'text-purple' : ''}`}></i>
                                <div>
                                  <div className="fw-bold">{doc.fileName}</div>
                                  <div className="text-secondary small">
                                    {(doc.fileSize / 1024).toFixed(0)} KB
                                    {doc.eSignStatus && (
                                      <span className="ms-2">
                                        <i className="ti ti-signature text-purple"></i> eSigned
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-blue-lt">
                                {docType?.label || 'Unknown'}
                              </span>
                            </td>
                            <td>
                              <div>
                                <div>{doc.uploadedBy}</div>
                                <div className="text-secondary small">
                                  {doc.uploadedByType === 'borrower' ? (
                                    <span className="badge bg-purple-lt">Borrower</span>
                                  ) : (
                                    <span className="badge bg-cyan-lt">Broker</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-secondary">
                              {new Date(doc.uploadedDate).toLocaleDateString()}
                              <div className="small">
                                {new Date(doc.uploadedDate).toLocaleTimeString()}
                              </div>
                            </td>
                            <td>
                              {linkedCondition ? (
                                <span className="badge bg-azure-lt">
                                  {linkedCondition.title}
                                </span>
                              ) : (
                                <span className="text-secondary">—</span>
                              )}
                            </td>
                            <td>
                              {doc.status === 'approved' && (
                                <span className="badge bg-green">Approved</span>
                              )}
                              {doc.status === 'pending' && (
                                <span className="badge bg-yellow">Pending</span>
                              )}
                              {doc.status === 'submitted' && (
                                <span className="badge bg-blue">Submitted</span>
                              )}
                              {doc.status === 'rejected' && (
                                <span className="badge bg-red">Rejected</span>
                              )}
                            </td>
                            <td>
                              {doc.aiSuggestions?.flags?.length > 0 ? (
                                <button 
                                  className="btn btn-sm btn-warning"
                                  onClick={() => {
                                    setSelectedDocument(doc);
                                    setShowAIModal(true);
                                  }}
                                >
                                  <i className="ti ti-alert-triangle me-1"></i>
                                  {doc.aiSuggestions.flags.length} Flag{doc.aiSuggestions.flags.length > 1 ? 's' : ''}
                                </button>
                              ) : (
                                <span className="text-secondary">—</span>
                              )}
                            </td>
                            <td>
                              <div className="btn-list">
                                <button className="btn btn-sm btn-ghost-primary">
                                  <i className="ti ti-eye"></i>
                                </button>
                                <button className="btn btn-sm btn-ghost-primary">
                                  <i className="ti ti-download"></i>
                                </button>
                                <button className="btn btn-sm btn-ghost-danger">
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Document Statistics */}
              <div className="row mt-4">
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Total Documents</div>
                      </div>
                      <div className="h2 mb-0">{documents.length}</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Approved</div>
                      </div>
                      <div className="h2 mb-0 text-green">
                        {documents.filter(d => d.status === 'approved').length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Pending Review</div>
                      </div>
                      <div className="h2 mb-0 text-yellow">
                        {documents.filter(d => d.status === 'pending' || d.status === 'submitted').length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Borrower Uploads</div>
                      </div>
                      <div className="h2 mb-0 text-purple">
                        {documents.filter(d => d.uploadedByType === 'borrower').length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Conditions Tab */}
          {activeTab === 'conditions' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">Underwriting Conditions ({conditions.length})</h3>
              </div>

              {/* Condition Summary Cards */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Open</div>
                      </div>
                      <div className="h2 mb-0 text-yellow">
                        {conditions.filter(c => c.status === 'open').length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Submitted</div>
                      </div>
                      <div className="h2 mb-0 text-blue">
                        {conditions.filter(c => c.status === 'submitted').length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Satisfied</div>
                      </div>
                      <div className="h2 mb-0 text-green">
                        {conditions.filter(c => c.status === 'satisfied').length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">Rejected</div>
                      </div>
                      <div className="h2 mb-0 text-red">
                        {conditions.filter(c => c.status === 'rejected').length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Conditions List */}
              {conditions.map(condition => (
                <div key={condition.id} className="card mb-3">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <h3 className="card-title mb-0">{condition.title}</h3>
                        {condition.priority === 'high' && (
                          <span className="badge bg-red ms-2">High Priority</span>
                        )}
                        {condition.priority === 'medium' && (
                          <span className="badge bg-yellow ms-2">Medium Priority</span>
                        )}
                      </div>
                      <div>
                        {condition.status === 'open' && (
                          <span className="badge bg-yellow-lt">Open</span>
                        )}
                        {condition.status === 'submitted' && (
                          <span className="badge bg-blue-lt">Submitted</span>
                        )}
                        {condition.status === 'satisfied' && (
                          <span className="badge bg-green-lt">Satisfied</span>
                        )}
                        {condition.status === 'rejected' && (
                          <span className="badge bg-red-lt">Rejected</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8">
                        <p className="text-secondary mb-3">{condition.description}</p>
                        
                        <div className="datagrid mb-3">
                          <div className="datagrid-item">
                            <div className="datagrid-title">Assigned To</div>
                            <div className="datagrid-content">
                              {condition.assignedTo === 'broker' ? (
                                <span className="badge bg-cyan-lt">Broker</span>
                              ) : (
                                <span className="badge bg-purple-lt">Borrower</span>
                              )}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">Due Date</div>
                            <div className="datagrid-content">
                              {new Date(condition.dueDate).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">Created</div>
                            <div className="datagrid-content">
                              {new Date(condition.createdDate).toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        {condition.attachedDocuments.length > 0 && (
                          <div className="mb-3">
                            <strong>Attached Documents:</strong>
                            <div className="mt-2">
                              {condition.attachedDocuments.map(docId => {
                                const doc = documents.find(d => d.id === docId);
                                return doc ? (
                                  <div key={docId} className="d-flex align-items-center mb-1">
                                    <i className="ti ti-file-text me-2"></i>
                                    <span>{doc.fileName}</span>
                                    <span className={`badge ms-2 ${
                                      doc.status === 'approved' ? 'bg-green' :
                                      doc.status === 'rejected' ? 'bg-red' :
                                      doc.status === 'submitted' ? 'bg-blue' : 'bg-yellow'
                                    }`}>
                                      {doc.status}
                                    </span>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}

                        {condition.submissionNotes && (
                          <div className="alert alert-info">
                            <strong>Submission Notes:</strong> {condition.submissionNotes}
                          </div>
                        )}

                        {condition.rejectionReason && (
                          <div className="alert alert-danger">
                            <strong>Rejection Reason:</strong> {condition.rejectionReason}
                          </div>
                        )}
                      </div>

                      <div className="col-md-4">
                        <h4 className="mb-2">History</h4>
                        <div className="list-group list-group-flush">
                          {condition.history.map((entry, idx) => (
                            <div key={idx} className="list-group-item px-0">
                              <div className="row align-items-center">
                                <div className="col-auto">
                                  <span className={`badge ${
                                    entry.action === 'satisfied' ? 'bg-green' :
                                    entry.action === 'rejected' ? 'bg-red' :
                                    entry.action === 'submitted' ? 'bg-blue' : 'bg-gray'
                                  }`}>
                                    {entry.action}
                                  </span>
                                </div>
                                <div className="col">
                                  <div className="small fw-bold">{entry.user}</div>
                                  <div className="text-secondary small">{entry.note}</div>
                                  <div className="text-secondary small">
                                    {new Date(entry.date).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {condition.status === 'open' || condition.status === 'rejected' ? (
                      <div className="mt-3">
                        <button 
                          className="btn btn-primary"
                          onClick={() => {
                            setSelectedCondition(condition);
                            setShowConditionModal(true);
                          }}
                        >
                          <i className="ti ti-upload me-1"></i>
                          {condition.status === 'rejected' ? 'Resubmit Condition' : 'Submit Condition'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Activity Timeline Tab */}
          {activeTab === 'activity' && (
            <div>
              <h3 className="card-title mb-3">Activity Timeline</h3>
              
              <div className="list-group list-group-flush">
                {activities.map(activity => (
                  <div key={activity.id} className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <span className={`avatar ${
                          activity.userType === 'borrower' ? 'bg-purple-lt' :
                          activity.userType === 'broker' ? 'bg-cyan-lt' :
                          activity.userType === 'underwriter' ? 'bg-green-lt' : 'bg-gray-lt'
                        }`}>
                          {activity.userType === 'borrower' && <i className="ti ti-user"></i>}
                          {activity.userType === 'broker' && <i className="ti ti-briefcase"></i>}
                          {activity.userType === 'underwriter' && <i className="ti ti-shield-check"></i>}
                          {activity.userType === 'system' && <i className="ti ti-robot"></i>}
                        </span>
                      </div>
                      <div className="col">
                        <div className="fw-bold">{activity.user}</div>
                        <div className="text-secondary">{activity.description}</div>
                        <div className="text-secondary small">
                          {new Date(activity.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div className="col-auto">
                        <span className={`badge ${
                          activity.type.includes('uploaded') ? 'bg-blue-lt' :
                          activity.type.includes('satisfied') || activity.type.includes('signed') ? 'bg-green-lt' :
                          activity.type.includes('rejected') ? 'bg-red-lt' :
                          activity.type.includes('submitted') ? 'bg-cyan-lt' :
                          activity.type.includes('ai') ? 'bg-purple-lt' : 'bg-gray-lt'
                        }`}>
                          {activity.type.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AUS Tab */}
          {activeTab === 'aus' && (
            <div>
              <DualAUSComponent 
                loan={loan}
                onAUSComplete={(selectedAUS, results) => {
                  console.log('AUS selected:', selectedAUS, results);
                }}
              />
            </div>
          )}

          {/* Fees & LE Tab */}
          {activeTab === 'fees' && (
            <div>
              <FeesWorksheetComponent loan={loan} />
            </div>
          )}

          {/* COC Tab */}
          {activeTab === 'coc' && (
            <div>
              {showCOCForm ? (
                <COCRequestForm
                  loan={loan}
                  onSubmit={handleCOCSubmit}
                  onCancel={() => setShowCOCForm(false)}
                />
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h3 className="mb-0">Change of Circumstance (COC)</h3>
                      <div className="text-secondary small mt-1">
                        TRID-compliant COC requests for lender review
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowCOCForm(true)}
                    >
                      <i className="ti ti-plus me-2"></i>
                      New COC Request
                    </button>
                  </div>
                  <COCRequestList
                    cocRequests={cocRequests}
                    onViewRequest={(request) => setSelectedCOCRequest(request)}
                  />
                </>
              )}
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="row">
              {/* Thread Sidebar */}
              <div className="col-md-3">
                <h4 className="mb-3">Threads</h4>
                <div className="list-group list-group-flush">
                  {chatThreads.map(thread => (
                    <a
                      key={thread.id}
                      href="#"
                      className={`list-group-item list-group-item-action ${activeThread === thread.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveThread(thread.id);
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div className="me-2">
                          <i className={`ti ${thread.icon}`}></i>
                        </div>
                        <div className="flex-fill">
                          <div className="fw-bold">{thread.name}</div>
                          <div className="text-secondary small text-truncate">
                            {thread.lastMessage}
                          </div>
                        </div>
                        {thread.unreadCount > 0 && (
                          <span className="badge bg-red">{thread.unreadCount}</span>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Chat Area */}
              <div className="col-md-9">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      {chatThreads.find(t => t.id === activeThread)?.name || 'Messages'}
                    </h3>
                    <div className="text-secondary small">
                      {chatThreads.find(t => t.id === activeThread)?.description}
                    </div>
                  </div>
                  
                  {/* Messages Container */}
                  <div className="card-body" style={{ height: '500px', overflowY: 'auto' }}>
                    <div className="list-group list-group-flush">
                      {chatMessages
                        .filter(msg => msg.threadId === activeThread)
                        .map(message => (
                          <div key={message.id} className="list-group-item border-0">
                            {message.messageType === 'system' ? (
                              // System Message
                              <div className="alert alert-info mb-0">
                                <div className="d-flex align-items-center">
                                  <div className="me-2">
                                    <i className={`ti ${
                                      message.systemEventType === 'approval' ? 'ti-circle-check' :
                                      message.systemEventType === 'lock' ? 'ti-lock' :
                                      message.systemEventType === 'ctc' ? 'ti-flag-check' : 'ti-bell'
                                    }`}></i>
                                  </div>
                                  <div className="flex-fill">
                                    <strong>{message.message}</strong>
                                    {message.systemEventType === 'lock' && message.metadata && (
                                      <div className="text-secondary small">
                                        Rate: {message.metadata.rate}% | 
                                        Period: {message.metadata.lockPeriod} days | 
                                        Expires: {new Date(message.metadata.expirationDate).toLocaleDateString()} | 
                                        Confirmation: {message.metadata.confirmation}
                                      </div>
                                    )}
                                    {message.systemEventType === 'approval' && message.metadata && (
                                      <div className="text-secondary small">
                                        Type: {message.metadata.approvalType} | 
                                        Conditions: {message.metadata.conditions.join(', ')} | 
                                        By: {message.metadata.approvedBy}
                                      </div>
                                    )}
                                    {message.systemEventType === 'ctc' && message.metadata && (
                                      <div className="text-secondary small">
                                        Closing Date: {new Date(message.metadata.closingDate).toLocaleDateString()} | 
                                        Cleared By: {message.metadata.clearedBy}
                                      </div>
                                    )}
                                    <div className="text-secondary small mt-1">
                                      {new Date(message.timestamp).toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // User Message
                              <div className={`d-flex ${message.senderType === 'broker' ? 'flex-row-reverse' : ''}`}>
                                <div className="me-2 ms-2">
                                  <span className={`avatar avatar-sm ${
                                    message.senderType === 'broker' ? 'bg-cyan-lt' : 'bg-blue-lt'
                                  }`}>
                                    {message.senderAvatar}
                                  </span>
                                </div>
                                <div className={`flex-fill ${message.senderType === 'broker' ? 'text-end' : ''}`} style={{ maxWidth: '70%' }}>
                                  <div className="fw-bold small">
                                    {message.senderName}
                                    {message.senderCompany && (
                                      <span className="text-secondary"> • {message.senderCompany}</span>
                                    )}
                                  </div>
                                  <div className={`card ${message.senderType === 'broker' ? 'bg-cyan-lt' : 'bg-blue-lt'} mt-1`}>
                                    <div className="card-body p-2">
                                      <div style={{ whiteSpace: 'pre-wrap' }}>{message.message}</div>
                                      
                                      {message.attachments && message.attachments.length > 0 && (
                                        <div className="mt-2">
                                          {message.attachments.map(file => (
                                            <div key={file.id} className="d-flex align-items-center mb-1">
                                              <i className="ti ti-paperclip me-1"></i>
                                              <a href="#" className="text-reset">
                                                {file.name}
                                              </a>
                                              <span className="text-secondary small ms-2">
                                                ({Math.round(file.size / 1024)} KB)
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-secondary small mt-1">
                                    {new Date(message.timestamp).toLocaleString()}
                                    {!message.isRead && message.senderType === 'lender' && (
                                      <span className="badge bg-red ms-2">New</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Message Composition */}
                  <div className="card-footer">
                    {attachedFiles.length > 0 && (
                      <div className="mb-2">
                        <div className="text-secondary small mb-1">Attached files:</div>
                        {attachedFiles.map((file, idx) => (
                          <div key={idx} className="d-inline-flex align-items-center me-2 mb-1">
                            <span className="badge bg-blue-lt">
                              <i className="ti ti-paperclip me-1"></i>
                              {file.name}
                              <button 
                                className="btn btn-sm btn-link text-reset p-0 ms-1"
                                onClick={() => setAttachedFiles(attachedFiles.filter((_, i) => i !== idx))}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="input-group">
                      <button 
                        className="btn btn-icon"
                        onClick={() => {
                          const mockFile = { name: 'document.pdf', size: 245000 };
                          setAttachedFiles([...attachedFiles, mockFile]);
                        }}
                        title="Attach file"
                      >
                        <i className="ti ti-paperclip"></i>
                      </button>
                      <textarea
                        className="form-control"
                        placeholder="Type your message..."
                        rows="2"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (newMessage.trim()) {
                              alert(`Message sent: ${newMessage}\n${attachedFiles.length} file(s) attached`);
                              setNewMessage('');
                              setAttachedFiles([]);
                            }
                          }
                        }}
                      ></textarea>
                      <button 
                        className="btn btn-primary"
                        onClick={() => {
                          if (newMessage.trim()) {
                            alert(`Message sent: ${newMessage}\n${attachedFiles.length} file(s) attached`);
                            setNewMessage('');
                            setAttachedFiles([]);
                          }
                        }}
                      >
                        <i className="ti ti-send"></i>
                      </button>
                    </div>
                    <div className="text-secondary small mt-1">
                      Press Enter to send, Shift+Enter for new line
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload Document</h5>
                <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Document File</label>
                  <input type="file" className="form-control" />
                  <small className="form-hint">Accepted formats: PDF, JPG, PNG. Max size: 10MB</small>
                </div>
                <div className="mb-3">
                  <label className="form-label required">Document Type</label>
                  <select className="form-select">
                    <option value="">Select document type...</option>
                    {documentTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} ({type.category})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Link to Condition (Optional)</label>
                  <select className="form-select">
                    <option value="">No condition</option>
                    {conditions.filter(c => c.status !== 'satisfied').map(condition => (
                      <option key={condition.id} value={condition.id}>
                        {condition.title} ({condition.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows="3" placeholder="Add any notes about this document..."></textarea>
                </div>
                <div className="alert alert-info">
                  <div className="d-flex">
                    <div><i className="ti ti-robot me-2"></i></div>
                    <div>
                      <strong>AI Assistant will analyze this document</strong>
                      <div className="text-secondary small">
                        After upload, AI will suggest document type, flag potential issues, and recommend conditions to link.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  // Simulate upload
                  alert('Document uploaded successfully! AI analysis will appear shortly.');
                  setShowUploadModal(false);
                }}>
                  <i className="ti ti-upload me-1"></i>
                  Upload Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Borrower Upload Link Modal */}
      {showBorrowerLinkModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Generate Borrower Upload Link</h5>
                <button type="button" className="btn-close" onClick={() => setShowBorrowerLinkModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Generate a secure link that allows the borrower to upload documents directly to this loan.</p>
                
                <div className="mb-3">
                  <label className="form-label">Link Expiration</label>
                  <select className="form-select">
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30" selected>30 days</option>
                    <option value="60">60 days</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Borrower Email</label>
                  <input type="email" className="form-control" value={loan?.borrower?.email || ''} readOnly />
                </div>

                <div className="alert alert-success">
                  <div className="d-flex">
                    <div><i className="ti ti-check me-2"></i></div>
                    <div>
                      <strong>Link Generated</strong>
                      <div className="text-secondary small mb-2">
                        Share this secure link with the borrower:
                      </div>
                      <div className="input-group">
                        <input 
                          type="text" 
                          className="form-control font-monospace" 
                          value={`https://uff.com/upload/${loan?.loanNumber}/abc123xyz`}
                          readOnly 
                        />
                        <button className="btn btn-icon" type="button">
                          <i className="ti ti-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="sendEmail" defaultChecked />
                  <label className="form-check-label" htmlFor="sendEmail">
                    Send link via email to borrower
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowBorrowerLinkModal(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('Link copied to clipboard and email sent to borrower!');
                  setShowBorrowerLinkModal(false);
                }}>
                  <i className="ti ti-send me-1"></i>
                  Send Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestions Modal */}
      {showAIModal && selectedDocument && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">AI Document Analysis</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowAIModal(false);
                  setSelectedDocument(null);
                }}></button>
              </div>
              <div className="modal-body">
                <h4>{selectedDocument.fileName}</h4>
                
                <div className="alert alert-info mb-3">
                  <div className="d-flex">
                    <div><i className="ti ti-robot me-2"></i></div>
                    <div>
                      <strong>AI Confidence: {(selectedDocument.aiSuggestions.confidence * 100).toFixed(0)}%</strong>
                      <div className="text-secondary small">
                        Suggested Type: {documentTypes.find(t => t.value === selectedDocument.aiSuggestions.suggestedType)?.label}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedDocument.aiSuggestions.flags.length > 0 && (
                  <div className="alert alert-warning">
                    <h4 className="alert-title">AI Detected Issues</h4>
                    <ul className="mb-0">
                      {selectedDocument.aiSuggestions.flags.map((flag, idx) => (
                        <li key={idx}>
                          {flag === 'missing_page_2' && 'Document appears to be missing page 2'}
                          {flag === 'quote_not_policy' && 'This appears to be a quote, not an actual policy'}
                          {flag === 'illegible' && 'Some text in this document is illegible'}
                          {flag === 'wrong_borrower' && 'Document may be for a different borrower'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Confirm Document Type</label>
                  <select className="form-select" defaultValue={selectedDocument.documentType}>
                    {documentTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} ({type.category})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Link to Condition</label>
                  <select className="form-select" defaultValue={selectedDocument.conditionId || ''}>
                    <option value="">No condition</option>
                    {conditions.filter(c => c.status !== 'satisfied').map(condition => (
                      <option key={condition.id} value={condition.id}>
                        {condition.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => {
                  setShowAIModal(false);
                  setSelectedDocument(null);
                }}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('AI suggestions confirmed and applied!');
                  setShowAIModal(false);
                  setSelectedDocument(null);
                }}>
                  <i className="ti ti-check me-1"></i>
                  Confirm & Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* eSign Modal */}
      {showESignModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Document for eSignature</h5>
                <button type="button" className="btn-close" onClick={() => setShowESignModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Document Template</label>
                  <select className="form-select">
                    <option value="">Select document...</option>
                    <option value="ssa89">SSA-89 Authorization</option>
                    <option value="credit_auth">Credit Authorization</option>
                    <option value="borrower_auth">Borrower Authorization</option>
                    <option value="disclosure">Lender Disclosure</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label required">Recipient</label>
                  <select className="form-select">
                    <option value="borrower">Primary Borrower ({loan?.borrower?.email})</option>
                    {loan?.coBorrower && (
                      <option value="coborrower">Co-Borrower ({loan.coBorrower.email})</option>
                    )}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Message to Borrower</label>
                  <textarea className="form-control" rows="3" placeholder="Optional message to include with the eSign request..."></textarea>
                </div>

                <div className="alert alert-info">
                  <div className="d-flex">
                    <div><i className="ti ti-signature me-2"></i></div>
                    <div>
                      <strong>eSignature Process</strong>
                      <div className="text-secondary small">
                        Borrower will receive an email with a secure link to review and sign the document. Once signed, the document will automatically be uploaded to this loan.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowESignModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('eSign request sent to borrower!');
                  setShowESignModal(false);
                }}>
                  <i className="ti ti-send me-1"></i>
                  Send for Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Condition Submission Modal */}
      {showConditionModal && selectedCondition && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Submit Condition: {selectedCondition.title}</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowConditionModal(false);
                  setSelectedCondition(null);
                }}></button>
              </div>
              <div className="modal-body">
                <p className="text-secondary">{selectedCondition.description}</p>

                <div className="mb-3">
                  <label className="form-label required">Attach Documents</label>
                  <select className="form-select" multiple size="5">
                    {documents.filter(d => !d.conditionId || d.conditionId === selectedCondition.id).map(doc => (
                      <option key={doc.id} value={doc.id}>
                        {doc.fileName} ({documentTypes.find(t => t.value === doc.documentType)?.label})
                      </option>
                    ))}
                  </select>
                  <small className="form-hint">Hold Ctrl/Cmd to select multiple documents</small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Submission Notes</label>
                  <textarea 
                    className="form-control" 
                    rows="3" 
                    placeholder="Add any explanatory notes for the underwriter..."
                  ></textarea>
                </div>

                {selectedCondition.status === 'rejected' && (
                  <div className="alert alert-warning">
                    <strong>Previous Rejection:</strong> {selectedCondition.rejectionReason}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => {
                  setShowConditionModal(false);
                  setSelectedCondition(null);
                }}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('Condition submitted for review!');
                  setShowConditionModal(false);
                  setSelectedCondition(null);
                }}>
                  <i className="ti ti-check me-1"></i>
                  Submit Condition
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Run Pricing Modal */}
      {showRunPricingModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Run Pricing Scenario</h5>
                <button type="button" className="btn-close" onClick={() => setShowRunPricingModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Scenario Name</label>
                  <input type="text" className="form-control" placeholder="e.g., 20-Year Fixed Option" />
                </div>

                <div className="mb-3">
                  <label className="form-label required">Loan Program</label>
                  <select className="form-select">
                    <option value="">Select program...</option>
                    {loanPrograms.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.name} - {program.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Loan Amount</label>
                      <input type="number" className="form-control" defaultValue={loan?.loanAmount || 450000} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Discount Points</label>
                      <select className="form-select">
                        <option value="0">0 Points (Par Rate)</option>
                        <option value="0.5">0.5 Points</option>
                        <option value="1">1.0 Points</option>
                        <option value="1.5">1.5 Points</option>
                        <option value="2">2.0 Points</option>
                        <option value="-0.5">-0.5 Points (Lender Credit)</option>
                        <option value="-1">-1.0 Points (Lender Credit)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info">
                  <div className="d-flex">
                    <div><i className="ti ti-info-circle me-2"></i></div>
                    <div>
                      <strong>Pricing Engine</strong>
                      <div className="text-secondary small">
                        Rates are calculated using current rate sheet (expires {new Date(mockRateSheet.expirationDate).toLocaleTimeString()}) 
                        plus adjustments for credit score, LTV, property type, and occupancy.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-warning">
                  <div className="d-flex">
                    <div><i className="ti ti-alert-triangle me-2"></i></div>
                    <div>
                      <strong>Rate Lock Required</strong>
                      <div className="text-secondary small">
                        Pricing is valid for today only. You must lock the rate to guarantee pricing beyond today's rate sheet expiration.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowRunPricingModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('Pricing scenario created! View results in the Pricing tab.');
                  setShowRunPricingModal(false);
                }}>
                  <i className="ti ti-calculator me-1"></i>
                  Run Pricing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lock Rate Modal */}
      {showLockRateModal && selectedScenario && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Lock Interest Rate</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowLockRateModal(false);
                  setSelectedScenario(null);
                }}></button>
              </div>
              <div className="modal-body">
                <h4 className="mb-3">{selectedScenario.scenarioName}</h4>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card bg-primary-lt">
                      <div className="card-body text-center">
                        <div className="text-secondary small">Interest Rate</div>
                        <div className="h1 mb-0">{selectedScenario.interestRate.toFixed(3)}%</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card bg-blue-lt">
                      <div className="card-body text-center">
                        <div className="text-secondary small">Monthly Payment</div>
                        <div className="h1 mb-0">${selectedScenario.monthlyPayment.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label required">Lock Period</label>
                  <select className="form-select">
                    <option value="15">15 Days</option>
                    <option value="30" selected>30 Days</option>
                    <option value="45">45 Days</option>
                    <option value="60">60 Days</option>
                    <option value="90">90 Days</option>
                  </select>
                  <small className="form-hint">Longer lock periods may have higher rates</small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Lock Expiration Date</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()} 
                    readOnly 
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes (Optional)</label>
                  <textarea className="form-control" rows="2" placeholder="Add any notes about this rate lock..."></textarea>
                </div>

                <div className="alert alert-success">
                  <div className="d-flex">
                    <div><i className="ti ti-lock me-2"></i></div>
                    <div>
                      <strong>Lock Confirmation</strong>
                      <div className="text-secondary small">
                        By locking this rate, you are guaranteeing the interest rate of {selectedScenario.interestRate.toFixed(3)}% 
                        for the selected lock period. The lock cannot be cancelled once confirmed.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-warning">
                  <div className="d-flex">
                    <div><i className="ti ti-alert-triangle me-2"></i></div>
                    <div>
                      <strong>Lock Expiration Alert</strong>
                      <div className="text-secondary small">
                        You will receive alerts 7 days and 3 days before lock expiration. 
                        If the loan does not close before expiration, you may need to re-lock at current market rates.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => {
                  setShowLockRateModal(false);
                  setSelectedScenario(null);
                }}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={() => {
                  alert(`Rate locked at ${selectedScenario.interestRate.toFixed(3)}% for 30 days!\nLock Confirmation: LOCK-2024-${loan?.id}-${Date.now()}`);
                  setShowLockRateModal(false);
                  setSelectedScenario(null);
                }}>
                  <i className="ti ti-lock me-1"></i>
                  Confirm Rate Lock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compare Scenarios Modal */}
      {showCompareModal && selectedScenarios.length >= 2 && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Compare Pricing Scenarios ({selectedScenarios.length})</h5>
                <button type="button" className="btn-close" onClick={() => setShowCompareModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-vcenter">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <th key={scenarioId} className="text-center">
                              {scenario.scenarioName}
                              {scenario.isSelected && <i className="ti ti-star-filled text-yellow ms-1"></i>}
                            </th>
                          ) : null;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="fw-bold">Program</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center">
                              <span className="badge bg-blue-lt">{scenario.programName}</span>
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr className="table-active">
                        <td className="fw-bold">Interest Rate</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center fw-bold text-primary">
                              {scenario.interestRate.toFixed(3)}%
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr>
                        <td className="fw-bold">APR</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center">
                              {scenario.apr.toFixed(3)}%
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr className="table-active">
                        <td className="fw-bold">Monthly Payment</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center fw-bold text-primary">
                              ${scenario.monthlyPayment.toLocaleString()}
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr>
                        <td className="fw-bold">Points</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center">
                              {scenario.points > 0 ? (
                                <span className="text-red">{scenario.points.toFixed(3)}</span>
                              ) : scenario.points < 0 ? (
                                <span className="text-green">{scenario.points.toFixed(3)}</span>
                              ) : (
                                <span>0.000</span>
                              )}
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr>
                        <td className="fw-bold">Closing Costs</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center">
                              ${scenario.closingCosts.toLocaleString()}
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr className="table-active">
                        <td className="fw-bold">Total Interest</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center text-secondary">
                              ${scenario.totalInterest.toLocaleString()}
                            </td>
                          ) : null;
                        })}
                      </tr>
                      <tr>
                        <td className="fw-bold">Lock Status</td>
                        {selectedScenarios.map(scenarioId => {
                          const scenario = pricingScenarios.find(s => s.id === scenarioId);
                          return scenario ? (
                            <td key={scenarioId} className="text-center">
                              {scenario.pricingDetails?.lockPeriod ? (
                                <span className="badge bg-green">
                                  Locked {scenario.pricingDetails.lockPeriod}d
                                </span>
                              ) : (
                                <span className="badge bg-gray-lt">Not Locked</span>
                              )}
                            </td>
                          ) : null;
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="alert alert-info mt-3">
                  <div className="d-flex">
                    <div><i className="ti ti-info-circle me-2"></i></div>
                    <div>
                      <strong>Comparison Notes</strong>
                      <div className="text-secondary small">
                        Lower monthly payment doesn't always mean lower total cost. Consider total interest paid over the life of the loan.
                        Discount points increase upfront costs but reduce the interest rate and total interest paid.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowCompareModal(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('Export comparison to PDF (future feature)');
                }}>
                  <i className="ti ti-download me-1"></i>
                  Export Comparison
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate Stack Modal */}
      {showRateStackModal && selectedScenario && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Rate Stack - {selectedScenario.programName}
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowRateStackModal(false);
                  setSelectedScenario(null);
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <h4 className="mb-3">Current Scenario</h4>
                    <div className="card bg-primary-lt">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-6">
                            <div className="text-secondary small">Scenario Name</div>
                            <div className="fw-bold">{selectedScenario.scenarioName}</div>
                          </div>
                          <div className="col-6">
                            <div className="text-secondary small">Current Rate</div>
                            <div className="h3 mb-0 text-primary">{selectedScenario.interestRate.toFixed(3)}%</div>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-6">
                            <div className="text-secondary small">Monthly Payment</div>
                            <div className="fw-bold">${selectedScenario.monthlyPayment.toLocaleString()}</div>
                          </div>
                          <div className="col-6">
                            <div className="text-secondary small">Points</div>
                            <div className="fw-bold">
                              {selectedScenario.points > 0 ? (
                                <span className="text-red">{selectedScenario.points.toFixed(3)}</span>
                              ) : selectedScenario.points < 0 ? (
                                <span className="text-green">{selectedScenario.points.toFixed(3)}</span>
                              ) : (
                                <span>0.000</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h4 className="mb-3">Rate Sheet Info</h4>
                    <div className="datagrid">
                      <div className="datagrid-item">
                        <div className="datagrid-title">Effective Date</div>
                        <div className="datagrid-content">
                          {new Date(mockRateSheet.effectiveDate).toLocaleString()}
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Expires</div>
                        <div className="datagrid-content text-red">
                          {new Date(mockRateSheet.expirationDate).toLocaleString()}
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">Loan Amount</div>
                        <div className="datagrid-content">
                          ${selectedScenario.loanAmount.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h4 className="mb-3">Available Rates for {selectedScenario.programName}</h4>
                
                {(() => {
                  const programRates = mockRateSheet.programs.find(p => p.programId === selectedScenario.programId);
                  
                  if (!programRates) {
                    return (
                      <div className="alert alert-warning">
                        <i className="ti ti-alert-triangle me-2"></i>
                        Rate stack not available for this program. Please contact your rate desk.
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="table-responsive">
                        <table className="table table-vcenter card-table table-hover">
                          <thead>
                            <tr>
                              <th>Rate</th>
                              <th>Points</th>
                              <th>APR</th>
                              <th>Price</th>
                              <th>Monthly Payment</th>
                              <th>Total Interest</th>
                              <th className="w-1"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {programRates.rates.map((rateOption, idx) => {
                              const isCurrentRate = Math.abs(rateOption.rate - selectedScenario.interestRate) < 0.001;
                              const monthlyPayment = Math.round(
                                (selectedScenario.loanAmount * (rateOption.rate / 100 / 12) * 
                                Math.pow(1 + rateOption.rate / 100 / 12, selectedScenario.programName.includes('15') ? 180 : 
                                selectedScenario.programName.includes('20') ? 240 : 360)) / 
                                (Math.pow(1 + rateOption.rate / 100 / 12, selectedScenario.programName.includes('15') ? 180 : 
                                selectedScenario.programName.includes('20') ? 240 : 360) - 1)
                              );
                              const term = selectedScenario.programName.includes('15') ? 180 : 
                                          selectedScenario.programName.includes('20') ? 240 : 360;
                              const totalInterest = Math.round((monthlyPayment * term) - selectedScenario.loanAmount);
                              
                              return (
                                <tr 
                                  key={idx} 
                                  className={isCurrentRate ? 'table-active' : ''}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td className="fw-bold">
                                    {isCurrentRate && <i className="ti ti-star-filled text-yellow me-2"></i>}
                                    {rateOption.rate.toFixed(3)}%
                                  </td>
                                  <td>
                                    {rateOption.points > 0 ? (
                                      <span className="badge bg-red-lt">
                                        {rateOption.points.toFixed(3)} pts
                                      </span>
                                    ) : rateOption.points < 0 ? (
                                      <span className="badge bg-green-lt">
                                        {rateOption.points.toFixed(3)} pts (credit)
                                      </span>
                                    ) : (
                                      <span className="badge bg-gray-lt">Par</span>
                                    )}
                                  </td>
                                  <td>{rateOption.apr.toFixed(3)}%</td>
                                  <td className="font-monospace">{rateOption.price.toFixed(3)}</td>
                                  <td className="fw-bold text-primary">
                                    ${monthlyPayment.toLocaleString()}/mo
                                  </td>
                                  <td className="text-secondary">
                                    ${totalInterest.toLocaleString()}
                                  </td>
                                  <td>
                                    {!isCurrentRate && (
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          alert(`Switch to ${rateOption.rate.toFixed(3)}% rate?\n\nThis will create a new pricing scenario with:\n- Rate: ${rateOption.rate.toFixed(3)}%\n- Payment: $${monthlyPayment.toLocaleString()}/mo\n- Points: ${rateOption.points.toFixed(3)}\n\n(Feature coming soon)`);
                                        }}
                                      >
                                        Select
                                      </button>
                                    )}
                                    {isCurrentRate && (
                                      <span className="badge bg-primary">Current</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="alert alert-info mt-3">
                        <div className="d-flex">
                          <div><i className="ti ti-info-circle me-2"></i></div>
                          <div>
                            <strong>Rate Stack Explanation</strong>
                            <div className="text-secondary small">
                              <strong>Points:</strong> Discount points (positive) lower your rate but increase upfront costs. 
                              Lender credits (negative) increase your rate but reduce closing costs.
                              <br />
                              <strong>Price:</strong> The price represents the percentage of the loan amount. 
                              100.000 = par (no points/credits). Below 100 = you pay points. Above 100 = you receive credits.
                              <br />
                              <strong>Total Interest:</strong> The total amount of interest you'll pay over the life of the loan.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="alert alert-warning mt-2">
                        <div className="d-flex">
                          <div><i className="ti ti-alert-triangle me-2"></i></div>
                          <div>
                            <strong>Rate Sheet Expiration</strong>
                            <div className="text-secondary small">
                              This rate sheet expires at {new Date(mockRateSheet.expirationDate).toLocaleTimeString()}. 
                              Rates shown are subject to change. Lock your rate to guarantee pricing.
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => {
                  setShowRateStackModal(false);
                  setSelectedScenario(null);
                }}>
                  Close
                </button>
                {!selectedScenario.pricingDetails?.lockPeriod && (
                  <button type="button" className="btn btn-primary" onClick={() => {
                    setShowRateStackModal(false);
                    setShowLockRateModal(true);
                  }}>
                    <i className="ti ti-lock me-1"></i>
                    Lock This Rate
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {showAIAnalysisModal && aiAnalysis && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-full-width modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ti ti-robot me-2"></i>
                  AI Virtual Underwriter Analysis
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowAIAnalysisModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Analysis Header */}
                <div className="row mb-4">
                  <div className="col-md-8">
                    <div className="alert alert-purple">
                      <div className="d-flex">
                        <div><i className="ti ti-info-circle me-2"></i></div>
                        <div>
                          <strong>Advisory Analysis Only</strong>
                          <div className="text-secondary small">
                            This AI analysis is advisory and does not constitute an underwriting decision. 
                            All recommendations must be reviewed by a licensed underwriter. 
                            Analysis Version: {aiAnalysis.analysisVersion} | 
                            Generated: {new Date(aiAnalysis.analysisDate).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-purple-lt">
                      <div className="card-body text-center">
                        <div className="text-secondary small">Overall Risk Score</div>
                        <div className="h1 mb-0">{aiAnalysis.overallRiskScore}/100</div>
                        <div className="text-uppercase small fw-bold text-purple">
                          {aiAnalysis.riskLevel} Risk
                        </div>
                        <div className="text-secondary small mt-2">
                          Confidence: {aiAnalysis.confidenceScore}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Flags */}
                <h3 className="mb-3">
                  <i className="ti ti-alert-triangle me-2"></i>
                  Risk Flags ({aiAnalysis.riskFlags.length})
                </h3>
                <div className="row mb-4">
                  {aiAnalysis.riskFlags.map(flag => (
                    <div key={flag.id} className="col-md-6 mb-3">
                      <div className={`card border-${
                        flag.severity === 'high' ? 'danger' :
                        flag.severity === 'medium' ? 'warning' : 'info'
                      }`}>
                        <div className="card-header">
                          <div className="d-flex align-items-center">
                            <span className={`badge bg-${
                              flag.severity === 'high' ? 'red' :
                              flag.severity === 'medium' ? 'yellow' : 'blue'
                            } me-2`}>
                              {flag.severity}
                            </span>
                            <span className="badge bg-gray-lt me-2">{flag.category}</span>
                            <h4 className="card-title mb-0">{flag.flag}</h4>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="mb-2">
                            <strong>Description:</strong>
                            <div className="text-secondary">{flag.description}</div>
                          </div>
                          <div className="mb-2">
                            <strong>Recommendation:</strong>
                            <div className="text-primary">{flag.recommendation}</div>
                          </div>
                          <div className="alert alert-warning mb-0">
                            <strong>Potential Impact:</strong> {flag.impact}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Improvement Suggestions */}
                <h3 className="mb-3">
                  <i className="ti ti-bulb me-2"></i>
                  Improvement Suggestions ({aiAnalysis.improvementSuggestions.length})
                </h3>
                <div className="table-responsive mb-4">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Suggestion</th>
                        <th>Potential Benefit</th>
                        <th>Tradeoff</th>
                        <th>Feasibility</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiAnalysis.improvementSuggestions.map(suggestion => (
                        <tr key={suggestion.id}>
                          <td>
                            <span className="badge bg-blue-lt">{suggestion.category}</span>
                          </td>
                          <td>
                            <div className="fw-bold">{suggestion.suggestion}</div>
                            <div className="text-secondary small">{suggestion.description}</div>
                          </td>
                          <td className="text-success">{suggestion.potentialBenefit}</td>
                          <td className="text-secondary">{suggestion.tradeoff}</td>
                          <td>
                            <span className={`badge ${
                              suggestion.feasibility === 'high' ? 'bg-green' :
                              suggestion.feasibility === 'medium' ? 'bg-yellow' : 'bg-red'
                            }`}>
                              {suggestion.feasibility}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Alternative Strategies */}
                <h3 className="mb-3">
                  <i className="ti ti-route me-2"></i>
                  Alternative Strategies ({aiAnalysis.alternativeStrategies.length})
                </h3>
                <div className="row mb-4">
                  {aiAnalysis.alternativeStrategies.map(strategy => (
                    <div key={strategy.id} className="col-md-4 mb-3">
                      <div className="card h-100">
                        <div className="card-header">
                          <h4 className="card-title">{strategy.strategy}</h4>
                          <div className="card-subtitle">{strategy.description}</div>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <strong className="text-success">Pros:</strong>
                            <ul className="mb-0">
                              {strategy.pros.map((pro, idx) => (
                                <li key={idx} className="text-secondary small">{pro}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="mb-3">
                            <strong className="text-danger">Cons:</strong>
                            <ul className="mb-0">
                              {strategy.cons.map((con, idx) => (
                                <li key={idx} className="text-secondary small">{con}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Estimated Savings:</strong>
                              <div className="text-primary">{strategy.estimatedSavings}</div>
                            </div>
                            <div>
                              <div className="text-secondary small">Score</div>
                              <div className="h3 mb-0">{strategy.recommendationScore}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Analysis Notes */}
                <div className="alert alert-info">
                  <h4 className="alert-title">
                    <i className="ti ti-notes me-2"></i>
                    Analysis Summary
                  </h4>
                  <div>{aiAnalysis.analysisNotes}</div>
                </div>

                {/* Disclaimer */}
                <div className="alert alert-warning mb-0">
                  <strong>Important Disclaimer:</strong> {aiAnalysis.disclaimer}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowAIAnalysisModal(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  alert('AI Analysis exported to PDF (future feature)');
                }}>
                  <i className="ti ti-download me-1"></i>
                  Export Analysis
                </button>
                <button type="button" className="btn btn-purple" onClick={() => {
                  alert('Share analysis with underwriter (future feature)');
                }}>
                  <i className="ti ti-share me-1"></i>
                  Share with Underwriter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Read-Only Notice */}
      <div className="alert alert-info mt-3">
        <div className="d-flex">
          <div>
            <i className="ti ti-lock me-2"></i>
          </div>
          <div>
            <h4 className="alert-title">Read-Only View</h4>
            <div className="text-secondary">
              You are viewing this loan in read-only mode. Editing capabilities will be available in a future update.
            </div>
          </div>
        </div>
      </div>

      {/* Floating AI Assistant */}
      <FloatingAIAssistant 
        activeTab={activeTab}
        loan={loan}
        lastAnalysis={aiAnalysis}
        onAnalyze={handleAIAnalysis}
        onLockChange={setIsAIPanelLocked}
      />

      {/* Self-Service Modals */}
      <PullCreditModal
        show={showPullCreditModal}
        onClose={() => setShowPullCreditModal(false)}
        onSubmit={handlePullCredit}
        borrowerName={`${loan.borrower.firstName} ${loan.borrower.lastName}`}
      />

      <OrderAppraisalModal
        show={showOrderAppraisalModal}
        onClose={() => setShowOrderAppraisalModal(false)}
        onSubmit={handleOrderAppraisal}
        property={{
          address: loan.property.address,
          propertyType: loan.property.propertyType,
          occupancy: loan.property.occupancy,
          estimatedValue: loan.property.estimatedValue
        }}
      />

      <OpenTitleModal
        show={showOpenTitleModal}
        onClose={() => setShowOpenTitleModal(false)}
        onSubmit={handleOpenTitle}
        property={{ address: loan.property.address }}
        borrower={{ name: `${loan.borrower.firstName} ${loan.borrower.lastName}` }}
      />

      <OrderVOAModal
        show={showOrderVOAModal}
        onClose={() => setShowOrderVOAModal(false)}
        onSubmit={handleOrderVOA}
        borrowerName={`${loan.borrower.firstName} ${loan.borrower.lastName}`}
      />

      <OrderVOIEModal
        show={showOrderVOIEModal}
        onClose={() => setShowOrderVOIEModal(false)}
        onSubmit={handleOrderVOIE}
        borrowerName={`${loan.borrower.firstName} ${loan.borrower.lastName}`}
        employer={loan.borrower.employment.employer}
      />

      {/* COC Request Detail Modal */}
      {selectedCOCRequest && (
        <COCRequestDetail
          request={selectedCOCRequest}
          onClose={() => setSelectedCOCRequest(null)}
        />
      )}
    </div>
  );
};

export default LoanDetailPage;
